import Vue from 'vue';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
var __assign = function () {
  __assign = Object.assign || function __assign(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
  var e = new Error(message);
  return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

function devAssert(condition, message) {
  const booleanCondition = Boolean(condition);
  if (!booleanCondition) {
    throw new Error(message);
  }
}

/**
 * Return true if `value` is object-like. A value is object-like if it's not
 * `null` and has a `typeof` result of "object".
 */
function isObjectLike(value) {
  return typeof value == 'object' && value !== null;
}

function invariant(condition, message) {
  const booleanCondition = Boolean(condition);
  if (!booleanCondition) {
    throw new Error(message != null ? message : 'Unexpected invariant triggered.');
  }
}

const LineRegExp = /\r\n|[\n\r]/g;
/**
 * Represents a location in a Source.
 */

/**
 * Takes a Source and a UTF-8 character offset, and returns the corresponding
 * line and column as a SourceLocation.
 */
function getLocation(source, position) {
  let lastLineStart = 0;
  let line = 1;
  for (const match of source.body.matchAll(LineRegExp)) {
    typeof match.index === 'number' || invariant(false);
    if (match.index >= position) {
      break;
    }
    lastLineStart = match.index + match[0].length;
    line += 1;
  }
  return {
    line,
    column: position + 1 - lastLineStart
  };
}

/**
 * Render a helpful description of the location in the GraphQL Source document.
 */
function printLocation(location) {
  return printSourceLocation(location.source, getLocation(location.source, location.start));
}
/**
 * Render a helpful description of the location in the GraphQL Source document.
 */

function printSourceLocation(source, sourceLocation) {
  const firstLineColumnOffset = source.locationOffset.column - 1;
  const body = ''.padStart(firstLineColumnOffset) + source.body;
  const lineIndex = sourceLocation.line - 1;
  const lineOffset = source.locationOffset.line - 1;
  const lineNum = sourceLocation.line + lineOffset;
  const columnOffset = sourceLocation.line === 1 ? firstLineColumnOffset : 0;
  const columnNum = sourceLocation.column + columnOffset;
  const locationStr = `${source.name}:${lineNum}:${columnNum}\n`;
  const lines = body.split(/\r\n|[\n\r]/g);
  const locationLine = lines[lineIndex]; // Special case for minified documents

  if (locationLine.length > 120) {
    const subLineIndex = Math.floor(columnNum / 80);
    const subLineColumnNum = columnNum % 80;
    const subLines = [];
    for (let i = 0; i < locationLine.length; i += 80) {
      subLines.push(locationLine.slice(i, i + 80));
    }
    return locationStr + printPrefixedLines([[`${lineNum} |`, subLines[0]], ...subLines.slice(1, subLineIndex + 1).map(subLine => ['|', subLine]), ['|', '^'.padStart(subLineColumnNum)], ['|', subLines[subLineIndex + 1]]]);
  }
  return locationStr + printPrefixedLines([
  // Lines specified like this: ["prefix", "string"],
  [`${lineNum - 1} |`, lines[lineIndex - 1]], [`${lineNum} |`, locationLine], ['|', '^'.padStart(columnNum)], [`${lineNum + 1} |`, lines[lineIndex + 1]]]);
}
function printPrefixedLines(lines) {
  const existingLines = lines.filter(([_, line]) => line !== undefined);
  const padLen = Math.max(...existingLines.map(([prefix]) => prefix.length));
  return existingLines.map(([prefix, line]) => prefix.padStart(padLen) + (line ? ' ' + line : '')).join('\n');
}

function toNormalizedOptions(args) {
  const firstArg = args[0];
  if (firstArg == null || 'kind' in firstArg || 'length' in firstArg) {
    return {
      nodes: firstArg,
      source: args[1],
      positions: args[2],
      path: args[3],
      originalError: args[4],
      extensions: args[5]
    };
  }
  return firstArg;
}
/**
 * A GraphQLError describes an Error found during the parse, validate, or
 * execute phases of performing a GraphQL operation. In addition to a message
 * and stack trace, it also includes information about the locations in a
 * GraphQL document and/or execution result that correspond to the Error.
 */

class GraphQLError extends Error {
  /**
   * An array of `{ line, column }` locations within the source GraphQL document
   * which correspond to this error.
   *
   * Errors during validation often contain multiple locations, for example to
   * point out two things with the same name. Errors during execution include a
   * single location, the field which produced the error.
   *
   * Enumerable, and appears in the result of JSON.stringify().
   */

  /**
   * An array describing the JSON-path into the execution response which
   * corresponds to this error. Only included for errors during execution.
   *
   * Enumerable, and appears in the result of JSON.stringify().
   */

  /**
   * An array of GraphQL AST Nodes corresponding to this error.
   */

  /**
   * The source GraphQL document for the first location of this error.
   *
   * Note that if this Error represents more than one node, the source may not
   * represent nodes after the first node.
   */

  /**
   * An array of character offsets within the source GraphQL document
   * which correspond to this error.
   */

  /**
   * The original error thrown from a field resolver during execution.
   */

  /**
   * Extension fields to add to the formatted error.
   */

  /**
   * @deprecated Please use the `GraphQLErrorOptions` constructor overload instead.
   */
  constructor(message, ...rawArgs) {
    var _this$nodes, _nodeLocations$, _ref;
    const {
      nodes,
      source,
      positions,
      path,
      originalError,
      extensions
    } = toNormalizedOptions(rawArgs);
    super(message);
    this.name = 'GraphQLError';
    this.path = path !== null && path !== void 0 ? path : undefined;
    this.originalError = originalError !== null && originalError !== void 0 ? originalError : undefined; // Compute list of blame nodes.

    this.nodes = undefinedIfEmpty(Array.isArray(nodes) ? nodes : nodes ? [nodes] : undefined);
    const nodeLocations = undefinedIfEmpty((_this$nodes = this.nodes) === null || _this$nodes === void 0 ? void 0 : _this$nodes.map(node => node.loc).filter(loc => loc != null)); // Compute locations in the source for the given nodes/positions.

    this.source = source !== null && source !== void 0 ? source : nodeLocations === null || nodeLocations === void 0 ? void 0 : (_nodeLocations$ = nodeLocations[0]) === null || _nodeLocations$ === void 0 ? void 0 : _nodeLocations$.source;
    this.positions = positions !== null && positions !== void 0 ? positions : nodeLocations === null || nodeLocations === void 0 ? void 0 : nodeLocations.map(loc => loc.start);
    this.locations = positions && source ? positions.map(pos => getLocation(source, pos)) : nodeLocations === null || nodeLocations === void 0 ? void 0 : nodeLocations.map(loc => getLocation(loc.source, loc.start));
    const originalExtensions = isObjectLike(originalError === null || originalError === void 0 ? void 0 : originalError.extensions) ? originalError === null || originalError === void 0 ? void 0 : originalError.extensions : undefined;
    this.extensions = (_ref = extensions !== null && extensions !== void 0 ? extensions : originalExtensions) !== null && _ref !== void 0 ? _ref : Object.create(null); // Only properties prescribed by the spec should be enumerable.
    // Keep the rest as non-enumerable.

    Object.defineProperties(this, {
      message: {
        writable: true,
        enumerable: true
      },
      name: {
        enumerable: false
      },
      nodes: {
        enumerable: false
      },
      source: {
        enumerable: false
      },
      positions: {
        enumerable: false
      },
      originalError: {
        enumerable: false
      }
    }); // Include (non-enumerable) stack trace.

    /* c8 ignore start */
    // FIXME: https://github.com/graphql/graphql-js/issues/2317

    if (originalError !== null && originalError !== void 0 && originalError.stack) {
      Object.defineProperty(this, 'stack', {
        value: originalError.stack,
        writable: true,
        configurable: true
      });
    } else if (Error.captureStackTrace) {
      Error.captureStackTrace(this, GraphQLError);
    } else {
      Object.defineProperty(this, 'stack', {
        value: Error().stack,
        writable: true,
        configurable: true
      });
    }
    /* c8 ignore stop */
  }
  get [Symbol.toStringTag]() {
    return 'GraphQLError';
  }
  toString() {
    let output = this.message;
    if (this.nodes) {
      for (const node of this.nodes) {
        if (node.loc) {
          output += '\n\n' + printLocation(node.loc);
        }
      }
    } else if (this.source && this.locations) {
      for (const location of this.locations) {
        output += '\n\n' + printSourceLocation(this.source, location);
      }
    }
    return output;
  }
  toJSON() {
    const formattedError = {
      message: this.message
    };
    if (this.locations != null) {
      formattedError.locations = this.locations;
    }
    if (this.path != null) {
      formattedError.path = this.path;
    }
    if (this.extensions != null && Object.keys(this.extensions).length > 0) {
      formattedError.extensions = this.extensions;
    }
    return formattedError;
  }
}
function undefinedIfEmpty(array) {
  return array === undefined || array.length === 0 ? undefined : array;
}

/**
 * Produces a GraphQLError representing a syntax error, containing useful
 * descriptive information about the syntax error's position in the source.
 */

function syntaxError(source, position, description) {
  return new GraphQLError(`Syntax Error: ${description}`, {
    source,
    positions: [position]
  });
}

/**
 * Contains a range of UTF-8 character offsets and token references that
 * identify the region of the source from which the AST derived.
 */
class Location {
  /**
   * The character offset at which this Node begins.
   */

  /**
   * The character offset at which this Node ends.
   */

  /**
   * The Token at which this Node begins.
   */

  /**
   * The Token at which this Node ends.
   */

  /**
   * The Source document the AST represents.
   */
  constructor(startToken, endToken, source) {
    this.start = startToken.start;
    this.end = endToken.end;
    this.startToken = startToken;
    this.endToken = endToken;
    this.source = source;
  }
  get [Symbol.toStringTag]() {
    return 'Location';
  }
  toJSON() {
    return {
      start: this.start,
      end: this.end
    };
  }
}
/**
 * Represents a range of characters represented by a lexical token
 * within a Source.
 */

class Token {
  /**
   * The kind of Token.
   */

  /**
   * The character offset at which this Node begins.
   */

  /**
   * The character offset at which this Node ends.
   */

  /**
   * The 1-indexed line number on which this Token appears.
   */

  /**
   * The 1-indexed column number at which this Token begins.
   */

  /**
   * For non-punctuation tokens, represents the interpreted value of the token.
   *
   * Note: is undefined for punctuation tokens, but typed as string for
   * convenience in the parser.
   */

  /**
   * Tokens exist as nodes in a double-linked-list amongst all tokens
   * including ignored tokens. <SOF> is always the first node and <EOF>
   * the last.
   */
  constructor(kind, start, end, line, column, value) {
    this.kind = kind;
    this.start = start;
    this.end = end;
    this.line = line;
    this.column = column; // eslint-disable-next-line @typescript-eslint/no-non-null-assertion

    this.value = value;
    this.prev = null;
    this.next = null;
  }
  get [Symbol.toStringTag]() {
    return 'Token';
  }
  toJSON() {
    return {
      kind: this.kind,
      value: this.value,
      line: this.line,
      column: this.column
    };
  }
}
/** Name */

var OperationTypeNode;
(function (OperationTypeNode) {
  OperationTypeNode['QUERY'] = 'query';
  OperationTypeNode['MUTATION'] = 'mutation';
  OperationTypeNode['SUBSCRIPTION'] = 'subscription';
})(OperationTypeNode || (OperationTypeNode = {}));

/**
 * The set of allowed directive location values.
 */
var DirectiveLocation;
(function (DirectiveLocation) {
  DirectiveLocation['QUERY'] = 'QUERY';
  DirectiveLocation['MUTATION'] = 'MUTATION';
  DirectiveLocation['SUBSCRIPTION'] = 'SUBSCRIPTION';
  DirectiveLocation['FIELD'] = 'FIELD';
  DirectiveLocation['FRAGMENT_DEFINITION'] = 'FRAGMENT_DEFINITION';
  DirectiveLocation['FRAGMENT_SPREAD'] = 'FRAGMENT_SPREAD';
  DirectiveLocation['INLINE_FRAGMENT'] = 'INLINE_FRAGMENT';
  DirectiveLocation['VARIABLE_DEFINITION'] = 'VARIABLE_DEFINITION';
  DirectiveLocation['SCHEMA'] = 'SCHEMA';
  DirectiveLocation['SCALAR'] = 'SCALAR';
  DirectiveLocation['OBJECT'] = 'OBJECT';
  DirectiveLocation['FIELD_DEFINITION'] = 'FIELD_DEFINITION';
  DirectiveLocation['ARGUMENT_DEFINITION'] = 'ARGUMENT_DEFINITION';
  DirectiveLocation['INTERFACE'] = 'INTERFACE';
  DirectiveLocation['UNION'] = 'UNION';
  DirectiveLocation['ENUM'] = 'ENUM';
  DirectiveLocation['ENUM_VALUE'] = 'ENUM_VALUE';
  DirectiveLocation['INPUT_OBJECT'] = 'INPUT_OBJECT';
  DirectiveLocation['INPUT_FIELD_DEFINITION'] = 'INPUT_FIELD_DEFINITION';
})(DirectiveLocation || (DirectiveLocation = {}));
/**
 * The enum type representing the directive location values.
 *
 * @deprecated Please use `DirectiveLocation`. Will be remove in v17.
 */

/**
 * The set of allowed kind values for AST nodes.
 */
var Kind;
(function (Kind) {
  Kind['NAME'] = 'Name';
  Kind['DOCUMENT'] = 'Document';
  Kind['OPERATION_DEFINITION'] = 'OperationDefinition';
  Kind['VARIABLE_DEFINITION'] = 'VariableDefinition';
  Kind['SELECTION_SET'] = 'SelectionSet';
  Kind['FIELD'] = 'Field';
  Kind['ARGUMENT'] = 'Argument';
  Kind['FRAGMENT_SPREAD'] = 'FragmentSpread';
  Kind['INLINE_FRAGMENT'] = 'InlineFragment';
  Kind['FRAGMENT_DEFINITION'] = 'FragmentDefinition';
  Kind['VARIABLE'] = 'Variable';
  Kind['INT'] = 'IntValue';
  Kind['FLOAT'] = 'FloatValue';
  Kind['STRING'] = 'StringValue';
  Kind['BOOLEAN'] = 'BooleanValue';
  Kind['NULL'] = 'NullValue';
  Kind['ENUM'] = 'EnumValue';
  Kind['LIST'] = 'ListValue';
  Kind['OBJECT'] = 'ObjectValue';
  Kind['OBJECT_FIELD'] = 'ObjectField';
  Kind['DIRECTIVE'] = 'Directive';
  Kind['NAMED_TYPE'] = 'NamedType';
  Kind['LIST_TYPE'] = 'ListType';
  Kind['NON_NULL_TYPE'] = 'NonNullType';
  Kind['SCHEMA_DEFINITION'] = 'SchemaDefinition';
  Kind['OPERATION_TYPE_DEFINITION'] = 'OperationTypeDefinition';
  Kind['SCALAR_TYPE_DEFINITION'] = 'ScalarTypeDefinition';
  Kind['OBJECT_TYPE_DEFINITION'] = 'ObjectTypeDefinition';
  Kind['FIELD_DEFINITION'] = 'FieldDefinition';
  Kind['INPUT_VALUE_DEFINITION'] = 'InputValueDefinition';
  Kind['INTERFACE_TYPE_DEFINITION'] = 'InterfaceTypeDefinition';
  Kind['UNION_TYPE_DEFINITION'] = 'UnionTypeDefinition';
  Kind['ENUM_TYPE_DEFINITION'] = 'EnumTypeDefinition';
  Kind['ENUM_VALUE_DEFINITION'] = 'EnumValueDefinition';
  Kind['INPUT_OBJECT_TYPE_DEFINITION'] = 'InputObjectTypeDefinition';
  Kind['DIRECTIVE_DEFINITION'] = 'DirectiveDefinition';
  Kind['SCHEMA_EXTENSION'] = 'SchemaExtension';
  Kind['SCALAR_TYPE_EXTENSION'] = 'ScalarTypeExtension';
  Kind['OBJECT_TYPE_EXTENSION'] = 'ObjectTypeExtension';
  Kind['INTERFACE_TYPE_EXTENSION'] = 'InterfaceTypeExtension';
  Kind['UNION_TYPE_EXTENSION'] = 'UnionTypeExtension';
  Kind['ENUM_TYPE_EXTENSION'] = 'EnumTypeExtension';
  Kind['INPUT_OBJECT_TYPE_EXTENSION'] = 'InputObjectTypeExtension';
})(Kind || (Kind = {}));
/**
 * The enum type representing the possible kind values of AST nodes.
 *
 * @deprecated Please use `Kind`. Will be remove in v17.
 */

/**
 * ```
 * WhiteSpace ::
 *   - "Horizontal Tab (U+0009)"
 *   - "Space (U+0020)"
 * ```
 * @internal
 */
function isWhiteSpace(code) {
  return code === 0x0009 || code === 0x0020;
}
/**
 * ```
 * Digit :: one of
 *   - `0` `1` `2` `3` `4` `5` `6` `7` `8` `9`
 * ```
 * @internal
 */

function isDigit(code) {
  return code >= 0x0030 && code <= 0x0039;
}
/**
 * ```
 * Letter :: one of
 *   - `A` `B` `C` `D` `E` `F` `G` `H` `I` `J` `K` `L` `M`
 *   - `N` `O` `P` `Q` `R` `S` `T` `U` `V` `W` `X` `Y` `Z`
 *   - `a` `b` `c` `d` `e` `f` `g` `h` `i` `j` `k` `l` `m`
 *   - `n` `o` `p` `q` `r` `s` `t` `u` `v` `w` `x` `y` `z`
 * ```
 * @internal
 */

function isLetter(code) {
  return code >= 0x0061 && code <= 0x007a ||
  // A-Z
  code >= 0x0041 && code <= 0x005a // a-z
  ;
}
/**
 * ```
 * NameStart ::
 *   - Letter
 *   - `_`
 * ```
 * @internal
 */

function isNameStart(code) {
  return isLetter(code) || code === 0x005f;
}
/**
 * ```
 * NameContinue ::
 *   - Letter
 *   - Digit
 *   - `_`
 * ```
 * @internal
 */

function isNameContinue(code) {
  return isLetter(code) || isDigit(code) || code === 0x005f;
}

/**
 * Produces the value of a block string from its parsed raw value, similar to
 * CoffeeScript's block string, Python's docstring trim or Ruby's strip_heredoc.
 *
 * This implements the GraphQL spec's BlockStringValue() static algorithm.
 *
 * @internal
 */

function dedentBlockStringLines(lines) {
  var _firstNonEmptyLine2;
  let commonIndent = Number.MAX_SAFE_INTEGER;
  let firstNonEmptyLine = null;
  let lastNonEmptyLine = -1;
  for (let i = 0; i < lines.length; ++i) {
    var _firstNonEmptyLine;
    const line = lines[i];
    const indent = leadingWhitespace(line);
    if (indent === line.length) {
      continue; // skip empty lines
    }
    firstNonEmptyLine = (_firstNonEmptyLine = firstNonEmptyLine) !== null && _firstNonEmptyLine !== void 0 ? _firstNonEmptyLine : i;
    lastNonEmptyLine = i;
    if (i !== 0 && indent < commonIndent) {
      commonIndent = indent;
    }
  }
  return lines // Remove common indentation from all lines but first.
  .map((line, i) => i === 0 ? line : line.slice(commonIndent)) // Remove leading and trailing blank lines.
  .slice((_firstNonEmptyLine2 = firstNonEmptyLine) !== null && _firstNonEmptyLine2 !== void 0 ? _firstNonEmptyLine2 : 0, lastNonEmptyLine + 1);
}
function leadingWhitespace(str) {
  let i = 0;
  while (i < str.length && isWhiteSpace(str.charCodeAt(i))) {
    ++i;
  }
  return i;
}

/**
 * An exported enum describing the different kinds of tokens that the
 * lexer emits.
 */
var TokenKind;
(function (TokenKind) {
  TokenKind['SOF'] = '<SOF>';
  TokenKind['EOF'] = '<EOF>';
  TokenKind['BANG'] = '!';
  TokenKind['DOLLAR'] = '$';
  TokenKind['AMP'] = '&';
  TokenKind['PAREN_L'] = '(';
  TokenKind['PAREN_R'] = ')';
  TokenKind['SPREAD'] = '...';
  TokenKind['COLON'] = ':';
  TokenKind['EQUALS'] = '=';
  TokenKind['AT'] = '@';
  TokenKind['BRACKET_L'] = '[';
  TokenKind['BRACKET_R'] = ']';
  TokenKind['BRACE_L'] = '{';
  TokenKind['PIPE'] = '|';
  TokenKind['BRACE_R'] = '}';
  TokenKind['NAME'] = 'Name';
  TokenKind['INT'] = 'Int';
  TokenKind['FLOAT'] = 'Float';
  TokenKind['STRING'] = 'String';
  TokenKind['BLOCK_STRING'] = 'BlockString';
  TokenKind['COMMENT'] = 'Comment';
})(TokenKind || (TokenKind = {}));
/**
 * The enum type representing the token kinds values.
 *
 * @deprecated Please use `TokenKind`. Will be remove in v17.
 */

/**
 * Given a Source object, creates a Lexer for that source.
 * A Lexer is a stateful stream generator in that every time
 * it is advanced, it returns the next token in the Source. Assuming the
 * source lexes, the final Token emitted by the lexer will be of kind
 * EOF, after which the lexer will repeatedly return the same EOF token
 * whenever called.
 */

class Lexer {
  /**
   * The previously focused non-ignored token.
   */

  /**
   * The currently focused non-ignored token.
   */

  /**
   * The (1-indexed) line containing the current token.
   */

  /**
   * The character offset at which the current line begins.
   */
  constructor(source) {
    const startOfFileToken = new Token(TokenKind.SOF, 0, 0, 0, 0);
    this.source = source;
    this.lastToken = startOfFileToken;
    this.token = startOfFileToken;
    this.line = 1;
    this.lineStart = 0;
  }
  get [Symbol.toStringTag]() {
    return 'Lexer';
  }
  /**
   * Advances the token stream to the next non-ignored token.
   */

  advance() {
    this.lastToken = this.token;
    const token = this.token = this.lookahead();
    return token;
  }
  /**
   * Looks ahead and returns the next non-ignored token, but does not change
   * the state of Lexer.
   */

  lookahead() {
    let token = this.token;
    if (token.kind !== TokenKind.EOF) {
      do {
        if (token.next) {
          token = token.next;
        } else {
          // Read the next token and form a link in the token linked-list.
          const nextToken = readNextToken(this, token.end); // @ts-expect-error next is only mutable during parsing.

          token.next = nextToken; // @ts-expect-error prev is only mutable during parsing.

          nextToken.prev = token;
          token = nextToken;
        }
      } while (token.kind === TokenKind.COMMENT);
    }
    return token;
  }
}
/**
 * @internal
 */

function isPunctuatorTokenKind(kind) {
  return kind === TokenKind.BANG || kind === TokenKind.DOLLAR || kind === TokenKind.AMP || kind === TokenKind.PAREN_L || kind === TokenKind.PAREN_R || kind === TokenKind.SPREAD || kind === TokenKind.COLON || kind === TokenKind.EQUALS || kind === TokenKind.AT || kind === TokenKind.BRACKET_L || kind === TokenKind.BRACKET_R || kind === TokenKind.BRACE_L || kind === TokenKind.PIPE || kind === TokenKind.BRACE_R;
}
/**
 * A Unicode scalar value is any Unicode code point except surrogate code
 * points. In other words, the inclusive ranges of values 0x0000 to 0xD7FF and
 * 0xE000 to 0x10FFFF.
 *
 * SourceCharacter ::
 *   - "Any Unicode scalar value"
 */

function isUnicodeScalarValue(code) {
  return code >= 0x0000 && code <= 0xd7ff || code >= 0xe000 && code <= 0x10ffff;
}
/**
 * The GraphQL specification defines source text as a sequence of unicode scalar
 * values (which Unicode defines to exclude surrogate code points). However
 * JavaScript defines strings as a sequence of UTF-16 code units which may
 * include surrogates. A surrogate pair is a valid source character as it
 * encodes a supplementary code point (above U+FFFF), but unpaired surrogate
 * code points are not valid source characters.
 */

function isSupplementaryCodePoint(body, location) {
  return isLeadingSurrogate(body.charCodeAt(location)) && isTrailingSurrogate(body.charCodeAt(location + 1));
}
function isLeadingSurrogate(code) {
  return code >= 0xd800 && code <= 0xdbff;
}
function isTrailingSurrogate(code) {
  return code >= 0xdc00 && code <= 0xdfff;
}
/**
 * Prints the code point (or end of file reference) at a given location in a
 * source for use in error messages.
 *
 * Printable ASCII is printed quoted, while other points are printed in Unicode
 * code point form (ie. U+1234).
 */

function printCodePointAt(lexer, location) {
  const code = lexer.source.body.codePointAt(location);
  if (code === undefined) {
    return TokenKind.EOF;
  } else if (code >= 0x0020 && code <= 0x007e) {
    // Printable ASCII
    const char = String.fromCodePoint(code);
    return char === '"' ? "'\"'" : `"${char}"`;
  } // Unicode code point

  return 'U+' + code.toString(16).toUpperCase().padStart(4, '0');
}
/**
 * Create a token with line and column location information.
 */

function createToken(lexer, kind, start, end, value) {
  const line = lexer.line;
  const col = 1 + start - lexer.lineStart;
  return new Token(kind, start, end, line, col, value);
}
/**
 * Gets the next token from the source starting at the given position.
 *
 * This skips over whitespace until it finds the next lexable token, then lexes
 * punctuators immediately or calls the appropriate helper function for more
 * complicated tokens.
 */

function readNextToken(lexer, start) {
  const body = lexer.source.body;
  const bodyLength = body.length;
  let position = start;
  while (position < bodyLength) {
    const code = body.charCodeAt(position); // SourceCharacter

    switch (code) {
      // Ignored ::
      //   - UnicodeBOM
      //   - WhiteSpace
      //   - LineTerminator
      //   - Comment
      //   - Comma
      //
      // UnicodeBOM :: "Byte Order Mark (U+FEFF)"
      //
      // WhiteSpace ::
      //   - "Horizontal Tab (U+0009)"
      //   - "Space (U+0020)"
      //
      // Comma :: ,
      case 0xfeff: // <BOM>

      case 0x0009: // \t

      case 0x0020: // <space>

      case 0x002c:
        // ,
        ++position;
        continue;
      // LineTerminator ::
      //   - "New Line (U+000A)"
      //   - "Carriage Return (U+000D)" [lookahead != "New Line (U+000A)"]
      //   - "Carriage Return (U+000D)" "New Line (U+000A)"

      case 0x000a:
        // \n
        ++position;
        ++lexer.line;
        lexer.lineStart = position;
        continue;
      case 0x000d:
        // \r
        if (body.charCodeAt(position + 1) === 0x000a) {
          position += 2;
        } else {
          ++position;
        }
        ++lexer.line;
        lexer.lineStart = position;
        continue;
      // Comment

      case 0x0023:
        // #
        return readComment(lexer, position);
      // Token ::
      //   - Punctuator
      //   - Name
      //   - IntValue
      //   - FloatValue
      //   - StringValue
      //
      // Punctuator :: one of ! $ & ( ) ... : = @ [ ] { | }

      case 0x0021:
        // !
        return createToken(lexer, TokenKind.BANG, position, position + 1);
      case 0x0024:
        // $
        return createToken(lexer, TokenKind.DOLLAR, position, position + 1);
      case 0x0026:
        // &
        return createToken(lexer, TokenKind.AMP, position, position + 1);
      case 0x0028:
        // (
        return createToken(lexer, TokenKind.PAREN_L, position, position + 1);
      case 0x0029:
        // )
        return createToken(lexer, TokenKind.PAREN_R, position, position + 1);
      case 0x002e:
        // .
        if (body.charCodeAt(position + 1) === 0x002e && body.charCodeAt(position + 2) === 0x002e) {
          return createToken(lexer, TokenKind.SPREAD, position, position + 3);
        }
        break;
      case 0x003a:
        // :
        return createToken(lexer, TokenKind.COLON, position, position + 1);
      case 0x003d:
        // =
        return createToken(lexer, TokenKind.EQUALS, position, position + 1);
      case 0x0040:
        // @
        return createToken(lexer, TokenKind.AT, position, position + 1);
      case 0x005b:
        // [
        return createToken(lexer, TokenKind.BRACKET_L, position, position + 1);
      case 0x005d:
        // ]
        return createToken(lexer, TokenKind.BRACKET_R, position, position + 1);
      case 0x007b:
        // {
        return createToken(lexer, TokenKind.BRACE_L, position, position + 1);
      case 0x007c:
        // |
        return createToken(lexer, TokenKind.PIPE, position, position + 1);
      case 0x007d:
        // }
        return createToken(lexer, TokenKind.BRACE_R, position, position + 1);
      // StringValue

      case 0x0022:
        // "
        if (body.charCodeAt(position + 1) === 0x0022 && body.charCodeAt(position + 2) === 0x0022) {
          return readBlockString(lexer, position);
        }
        return readString(lexer, position);
    } // IntValue | FloatValue (Digit | -)

    if (isDigit(code) || code === 0x002d) {
      return readNumber(lexer, position, code);
    } // Name

    if (isNameStart(code)) {
      return readName(lexer, position);
    }
    throw syntaxError(lexer.source, position, code === 0x0027 ? 'Unexpected single quote character (\'), did you mean to use a double quote (")?' : isUnicodeScalarValue(code) || isSupplementaryCodePoint(body, position) ? `Unexpected character: ${printCodePointAt(lexer, position)}.` : `Invalid character: ${printCodePointAt(lexer, position)}.`);
  }
  return createToken(lexer, TokenKind.EOF, bodyLength, bodyLength);
}
/**
 * Reads a comment token from the source file.
 *
 * ```
 * Comment :: # CommentChar* [lookahead != CommentChar]
 *
 * CommentChar :: SourceCharacter but not LineTerminator
 * ```
 */

function readComment(lexer, start) {
  const body = lexer.source.body;
  const bodyLength = body.length;
  let position = start + 1;
  while (position < bodyLength) {
    const code = body.charCodeAt(position); // LineTerminator (\n | \r)

    if (code === 0x000a || code === 0x000d) {
      break;
    } // SourceCharacter

    if (isUnicodeScalarValue(code)) {
      ++position;
    } else if (isSupplementaryCodePoint(body, position)) {
      position += 2;
    } else {
      break;
    }
  }
  return createToken(lexer, TokenKind.COMMENT, start, position, body.slice(start + 1, position));
}
/**
 * Reads a number token from the source file, either a FloatValue or an IntValue
 * depending on whether a FractionalPart or ExponentPart is encountered.
 *
 * ```
 * IntValue :: IntegerPart [lookahead != {Digit, `.`, NameStart}]
 *
 * IntegerPart ::
 *   - NegativeSign? 0
 *   - NegativeSign? NonZeroDigit Digit*
 *
 * NegativeSign :: -
 *
 * NonZeroDigit :: Digit but not `0`
 *
 * FloatValue ::
 *   - IntegerPart FractionalPart ExponentPart [lookahead != {Digit, `.`, NameStart}]
 *   - IntegerPart FractionalPart [lookahead != {Digit, `.`, NameStart}]
 *   - IntegerPart ExponentPart [lookahead != {Digit, `.`, NameStart}]
 *
 * FractionalPart :: . Digit+
 *
 * ExponentPart :: ExponentIndicator Sign? Digit+
 *
 * ExponentIndicator :: one of `e` `E`
 *
 * Sign :: one of + -
 * ```
 */

function readNumber(lexer, start, firstCode) {
  const body = lexer.source.body;
  let position = start;
  let code = firstCode;
  let isFloat = false; // NegativeSign (-)

  if (code === 0x002d) {
    code = body.charCodeAt(++position);
  } // Zero (0)

  if (code === 0x0030) {
    code = body.charCodeAt(++position);
    if (isDigit(code)) {
      throw syntaxError(lexer.source, position, `Invalid number, unexpected digit after 0: ${printCodePointAt(lexer, position)}.`);
    }
  } else {
    position = readDigits(lexer, position, code);
    code = body.charCodeAt(position);
  } // Full stop (.)

  if (code === 0x002e) {
    isFloat = true;
    code = body.charCodeAt(++position);
    position = readDigits(lexer, position, code);
    code = body.charCodeAt(position);
  } // E e

  if (code === 0x0045 || code === 0x0065) {
    isFloat = true;
    code = body.charCodeAt(++position); // + -

    if (code === 0x002b || code === 0x002d) {
      code = body.charCodeAt(++position);
    }
    position = readDigits(lexer, position, code);
    code = body.charCodeAt(position);
  } // Numbers cannot be followed by . or NameStart

  if (code === 0x002e || isNameStart(code)) {
    throw syntaxError(lexer.source, position, `Invalid number, expected digit but got: ${printCodePointAt(lexer, position)}.`);
  }
  return createToken(lexer, isFloat ? TokenKind.FLOAT : TokenKind.INT, start, position, body.slice(start, position));
}
/**
 * Returns the new position in the source after reading one or more digits.
 */

function readDigits(lexer, start, firstCode) {
  if (!isDigit(firstCode)) {
    throw syntaxError(lexer.source, start, `Invalid number, expected digit but got: ${printCodePointAt(lexer, start)}.`);
  }
  const body = lexer.source.body;
  let position = start + 1; // +1 to skip first firstCode

  while (isDigit(body.charCodeAt(position))) {
    ++position;
  }
  return position;
}
/**
 * Reads a single-quote string token from the source file.
 *
 * ```
 * StringValue ::
 *   - `""` [lookahead != `"`]
 *   - `"` StringCharacter+ `"`
 *
 * StringCharacter ::
 *   - SourceCharacter but not `"` or `\` or LineTerminator
 *   - `\u` EscapedUnicode
 *   - `\` EscapedCharacter
 *
 * EscapedUnicode ::
 *   - `{` HexDigit+ `}`
 *   - HexDigit HexDigit HexDigit HexDigit
 *
 * EscapedCharacter :: one of `"` `\` `/` `b` `f` `n` `r` `t`
 * ```
 */

function readString(lexer, start) {
  const body = lexer.source.body;
  const bodyLength = body.length;
  let position = start + 1;
  let chunkStart = position;
  let value = '';
  while (position < bodyLength) {
    const code = body.charCodeAt(position); // Closing Quote (")

    if (code === 0x0022) {
      value += body.slice(chunkStart, position);
      return createToken(lexer, TokenKind.STRING, start, position + 1, value);
    } // Escape Sequence (\)

    if (code === 0x005c) {
      value += body.slice(chunkStart, position);
      const escape = body.charCodeAt(position + 1) === 0x0075 // u
      ? body.charCodeAt(position + 2) === 0x007b // {
      ? readEscapedUnicodeVariableWidth(lexer, position) : readEscapedUnicodeFixedWidth(lexer, position) : readEscapedCharacter(lexer, position);
      value += escape.value;
      position += escape.size;
      chunkStart = position;
      continue;
    } // LineTerminator (\n | \r)

    if (code === 0x000a || code === 0x000d) {
      break;
    } // SourceCharacter

    if (isUnicodeScalarValue(code)) {
      ++position;
    } else if (isSupplementaryCodePoint(body, position)) {
      position += 2;
    } else {
      throw syntaxError(lexer.source, position, `Invalid character within String: ${printCodePointAt(lexer, position)}.`);
    }
  }
  throw syntaxError(lexer.source, position, 'Unterminated string.');
} // The string value and lexed size of an escape sequence.

function readEscapedUnicodeVariableWidth(lexer, position) {
  const body = lexer.source.body;
  let point = 0;
  let size = 3; // Cannot be larger than 12 chars (\u{00000000}).

  while (size < 12) {
    const code = body.charCodeAt(position + size++); // Closing Brace (})

    if (code === 0x007d) {
      // Must be at least 5 chars (\u{0}) and encode a Unicode scalar value.
      if (size < 5 || !isUnicodeScalarValue(point)) {
        break;
      }
      return {
        value: String.fromCodePoint(point),
        size
      };
    } // Append this hex digit to the code point.

    point = point << 4 | readHexDigit(code);
    if (point < 0) {
      break;
    }
  }
  throw syntaxError(lexer.source, position, `Invalid Unicode escape sequence: "${body.slice(position, position + size)}".`);
}
function readEscapedUnicodeFixedWidth(lexer, position) {
  const body = lexer.source.body;
  const code = read16BitHexCode(body, position + 2);
  if (isUnicodeScalarValue(code)) {
    return {
      value: String.fromCodePoint(code),
      size: 6
    };
  } // GraphQL allows JSON-style surrogate pair escape sequences, but only when
  // a valid pair is formed.

  if (isLeadingSurrogate(code)) {
    // \u
    if (body.charCodeAt(position + 6) === 0x005c && body.charCodeAt(position + 7) === 0x0075) {
      const trailingCode = read16BitHexCode(body, position + 8);
      if (isTrailingSurrogate(trailingCode)) {
        // JavaScript defines strings as a sequence of UTF-16 code units and
        // encodes Unicode code points above U+FFFF using a surrogate pair of
        // code units. Since this is a surrogate pair escape sequence, just
        // include both codes into the JavaScript string value. Had JavaScript
        // not been internally based on UTF-16, then this surrogate pair would
        // be decoded to retrieve the supplementary code point.
        return {
          value: String.fromCodePoint(code, trailingCode),
          size: 12
        };
      }
    }
  }
  throw syntaxError(lexer.source, position, `Invalid Unicode escape sequence: "${body.slice(position, position + 6)}".`);
}
/**
 * Reads four hexadecimal characters and returns the positive integer that 16bit
 * hexadecimal string represents. For example, "000f" will return 15, and "dead"
 * will return 57005.
 *
 * Returns a negative number if any char was not a valid hexadecimal digit.
 */

function read16BitHexCode(body, position) {
  // readHexDigit() returns -1 on error. ORing a negative value with any other
  // value always produces a negative value.
  return readHexDigit(body.charCodeAt(position)) << 12 | readHexDigit(body.charCodeAt(position + 1)) << 8 | readHexDigit(body.charCodeAt(position + 2)) << 4 | readHexDigit(body.charCodeAt(position + 3));
}
/**
 * Reads a hexadecimal character and returns its positive integer value (0-15).
 *
 * '0' becomes 0, '9' becomes 9
 * 'A' becomes 10, 'F' becomes 15
 * 'a' becomes 10, 'f' becomes 15
 *
 * Returns -1 if the provided character code was not a valid hexadecimal digit.
 *
 * HexDigit :: one of
 *   - `0` `1` `2` `3` `4` `5` `6` `7` `8` `9`
 *   - `A` `B` `C` `D` `E` `F`
 *   - `a` `b` `c` `d` `e` `f`
 */

function readHexDigit(code) {
  return code >= 0x0030 && code <= 0x0039 // 0-9
  ? code - 0x0030 : code >= 0x0041 && code <= 0x0046 // A-F
  ? code - 0x0037 : code >= 0x0061 && code <= 0x0066 // a-f
  ? code - 0x0057 : -1;
}
/**
 * | Escaped Character | Code Point | Character Name               |
 * | ----------------- | ---------- | ---------------------------- |
 * | `"`               | U+0022     | double quote                 |
 * | `\`               | U+005C     | reverse solidus (back slash) |
 * | `/`               | U+002F     | solidus (forward slash)      |
 * | `b`               | U+0008     | backspace                    |
 * | `f`               | U+000C     | form feed                    |
 * | `n`               | U+000A     | line feed (new line)         |
 * | `r`               | U+000D     | carriage return              |
 * | `t`               | U+0009     | horizontal tab               |
 */

function readEscapedCharacter(lexer, position) {
  const body = lexer.source.body;
  const code = body.charCodeAt(position + 1);
  switch (code) {
    case 0x0022:
      // "
      return {
        value: '\u0022',
        size: 2
      };
    case 0x005c:
      // \
      return {
        value: '\u005c',
        size: 2
      };
    case 0x002f:
      // /
      return {
        value: '\u002f',
        size: 2
      };
    case 0x0062:
      // b
      return {
        value: '\u0008',
        size: 2
      };
    case 0x0066:
      // f
      return {
        value: '\u000c',
        size: 2
      };
    case 0x006e:
      // n
      return {
        value: '\u000a',
        size: 2
      };
    case 0x0072:
      // r
      return {
        value: '\u000d',
        size: 2
      };
    case 0x0074:
      // t
      return {
        value: '\u0009',
        size: 2
      };
  }
  throw syntaxError(lexer.source, position, `Invalid character escape sequence: "${body.slice(position, position + 2)}".`);
}
/**
 * Reads a block string token from the source file.
 *
 * ```
 * StringValue ::
 *   - `"""` BlockStringCharacter* `"""`
 *
 * BlockStringCharacter ::
 *   - SourceCharacter but not `"""` or `\"""`
 *   - `\"""`
 * ```
 */

function readBlockString(lexer, start) {
  const body = lexer.source.body;
  const bodyLength = body.length;
  let lineStart = lexer.lineStart;
  let position = start + 3;
  let chunkStart = position;
  let currentLine = '';
  const blockLines = [];
  while (position < bodyLength) {
    const code = body.charCodeAt(position); // Closing Triple-Quote (""")

    if (code === 0x0022 && body.charCodeAt(position + 1) === 0x0022 && body.charCodeAt(position + 2) === 0x0022) {
      currentLine += body.slice(chunkStart, position);
      blockLines.push(currentLine);
      const token = createToken(lexer, TokenKind.BLOCK_STRING, start, position + 3,
      // Return a string of the lines joined with U+000A.
      dedentBlockStringLines(blockLines).join('\n'));
      lexer.line += blockLines.length - 1;
      lexer.lineStart = lineStart;
      return token;
    } // Escaped Triple-Quote (\""")

    if (code === 0x005c && body.charCodeAt(position + 1) === 0x0022 && body.charCodeAt(position + 2) === 0x0022 && body.charCodeAt(position + 3) === 0x0022) {
      currentLine += body.slice(chunkStart, position);
      chunkStart = position + 1; // skip only slash

      position += 4;
      continue;
    } // LineTerminator

    if (code === 0x000a || code === 0x000d) {
      currentLine += body.slice(chunkStart, position);
      blockLines.push(currentLine);
      if (code === 0x000d && body.charCodeAt(position + 1) === 0x000a) {
        position += 2;
      } else {
        ++position;
      }
      currentLine = '';
      chunkStart = position;
      lineStart = position;
      continue;
    } // SourceCharacter

    if (isUnicodeScalarValue(code)) {
      ++position;
    } else if (isSupplementaryCodePoint(body, position)) {
      position += 2;
    } else {
      throw syntaxError(lexer.source, position, `Invalid character within String: ${printCodePointAt(lexer, position)}.`);
    }
  }
  throw syntaxError(lexer.source, position, 'Unterminated string.');
}
/**
 * Reads an alphanumeric + underscore name from the source.
 *
 * ```
 * Name ::
 *   - NameStart NameContinue* [lookahead != NameContinue]
 * ```
 */

function readName(lexer, start) {
  const body = lexer.source.body;
  const bodyLength = body.length;
  let position = start + 1;
  while (position < bodyLength) {
    const code = body.charCodeAt(position);
    if (isNameContinue(code)) {
      ++position;
    } else {
      break;
    }
  }
  return createToken(lexer, TokenKind.NAME, start, position, body.slice(start, position));
}

const MAX_ARRAY_LENGTH = 10;
const MAX_RECURSIVE_DEPTH = 2;
/**
 * Used to print values in error messages.
 */

function inspect(value) {
  return formatValue(value, []);
}
function formatValue(value, seenValues) {
  switch (typeof value) {
    case 'string':
      return JSON.stringify(value);
    case 'function':
      return value.name ? `[function ${value.name}]` : '[function]';
    case 'object':
      return formatObjectValue(value, seenValues);
    default:
      return String(value);
  }
}
function formatObjectValue(value, previouslySeenValues) {
  if (value === null) {
    return 'null';
  }
  if (previouslySeenValues.includes(value)) {
    return '[Circular]';
  }
  const seenValues = [...previouslySeenValues, value];
  if (isJSONable(value)) {
    const jsonValue = value.toJSON(); // check for infinite recursion

    if (jsonValue !== value) {
      return typeof jsonValue === 'string' ? jsonValue : formatValue(jsonValue, seenValues);
    }
  } else if (Array.isArray(value)) {
    return formatArray(value, seenValues);
  }
  return formatObject(value, seenValues);
}
function isJSONable(value) {
  return typeof value.toJSON === 'function';
}
function formatObject(object, seenValues) {
  const entries = Object.entries(object);
  if (entries.length === 0) {
    return '{}';
  }
  if (seenValues.length > MAX_RECURSIVE_DEPTH) {
    return '[' + getObjectTag(object) + ']';
  }
  const properties = entries.map(([key, value]) => key + ': ' + formatValue(value, seenValues));
  return '{ ' + properties.join(', ') + ' }';
}
function formatArray(array, seenValues) {
  if (array.length === 0) {
    return '[]';
  }
  if (seenValues.length > MAX_RECURSIVE_DEPTH) {
    return '[Array]';
  }
  const len = Math.min(MAX_ARRAY_LENGTH, array.length);
  const remaining = array.length - len;
  const items = [];
  for (let i = 0; i < len; ++i) {
    items.push(formatValue(array[i], seenValues));
  }
  if (remaining === 1) {
    items.push('... 1 more item');
  } else if (remaining > 1) {
    items.push(`... ${remaining} more items`);
  }
  return '[' + items.join(', ') + ']';
}
function getObjectTag(object) {
  const tag = Object.prototype.toString.call(object).replace(/^\[object /, '').replace(/]$/, '');
  if (tag === 'Object' && typeof object.constructor === 'function') {
    const name = object.constructor.name;
    if (typeof name === 'string' && name !== '') {
      return name;
    }
  }
  return tag;
}

/**
 * A replacement for instanceof which includes an error warning when multi-realm
 * constructors are detected.
 * See: https://expressjs.com/en/advanced/best-practice-performance.html#set-node_env-to-production
 * See: https://webpack.js.org/guides/production/
 */

const instanceOf = /* c8 ignore next 6 */
// FIXME: https://github.com/graphql/graphql-js/issues/2317
// eslint-disable-next-line no-undef
function instanceOf(value, constructor) {
  return value instanceof constructor;
} ;

/**
 * A representation of source input to GraphQL. The `name` and `locationOffset` parameters are
 * optional, but they are useful for clients who store GraphQL documents in source files.
 * For example, if the GraphQL input starts at line 40 in a file named `Foo.graphql`, it might
 * be useful for `name` to be `"Foo.graphql"` and location to be `{ line: 40, column: 1 }`.
 * The `line` and `column` properties in `locationOffset` are 1-indexed.
 */
class Source {
  constructor(body, name = 'GraphQL request', locationOffset = {
    line: 1,
    column: 1
  }) {
    typeof body === 'string' || devAssert(false, `Body must be a string. Received: ${inspect(body)}.`);
    this.body = body;
    this.name = name;
    this.locationOffset = locationOffset;
    this.locationOffset.line > 0 || devAssert(false, 'line in locationOffset is 1-indexed and must be positive.');
    this.locationOffset.column > 0 || devAssert(false, 'column in locationOffset is 1-indexed and must be positive.');
  }
  get [Symbol.toStringTag]() {
    return 'Source';
  }
}
/**
 * Test if the given value is a Source object.
 *
 * @internal
 */

function isSource(source) {
  return instanceOf(source, Source);
}

/**
 * Configuration options to control parser behavior
 */

/**
 * Given a GraphQL source, parses it into a Document.
 * Throws GraphQLError if a syntax error is encountered.
 */
function parse(source, options) {
  const parser = new Parser(source, options);
  return parser.parseDocument();
}
/**
 * This class is exported only to assist people in implementing their own parsers
 * without duplicating too much code and should be used only as last resort for cases
 * such as experimental syntax or if certain features could not be contributed upstream.
 *
 * It is still part of the internal API and is versioned, so any changes to it are never
 * considered breaking changes. If you still need to support multiple versions of the
 * library, please use the `versionInfo` variable for version detection.
 *
 * @internal
 */

class Parser {
  constructor(source, options = {}) {
    const sourceObj = isSource(source) ? source : new Source(source);
    this._lexer = new Lexer(sourceObj);
    this._options = options;
    this._tokenCounter = 0;
  }
  /**
   * Converts a name lex token into a name parse node.
   */

  parseName() {
    const token = this.expectToken(TokenKind.NAME);
    return this.node(token, {
      kind: Kind.NAME,
      value: token.value
    });
  } // Implements the parsing rules in the Document section.

  /**
   * Document : Definition+
   */

  parseDocument() {
    return this.node(this._lexer.token, {
      kind: Kind.DOCUMENT,
      definitions: this.many(TokenKind.SOF, this.parseDefinition, TokenKind.EOF)
    });
  }
  /**
   * Definition :
   *   - ExecutableDefinition
   *   - TypeSystemDefinition
   *   - TypeSystemExtension
   *
   * ExecutableDefinition :
   *   - OperationDefinition
   *   - FragmentDefinition
   *
   * TypeSystemDefinition :
   *   - SchemaDefinition
   *   - TypeDefinition
   *   - DirectiveDefinition
   *
   * TypeDefinition :
   *   - ScalarTypeDefinition
   *   - ObjectTypeDefinition
   *   - InterfaceTypeDefinition
   *   - UnionTypeDefinition
   *   - EnumTypeDefinition
   *   - InputObjectTypeDefinition
   */

  parseDefinition() {
    if (this.peek(TokenKind.BRACE_L)) {
      return this.parseOperationDefinition();
    } // Many definitions begin with a description and require a lookahead.

    const hasDescription = this.peekDescription();
    const keywordToken = hasDescription ? this._lexer.lookahead() : this._lexer.token;
    if (keywordToken.kind === TokenKind.NAME) {
      switch (keywordToken.value) {
        case 'schema':
          return this.parseSchemaDefinition();
        case 'scalar':
          return this.parseScalarTypeDefinition();
        case 'type':
          return this.parseObjectTypeDefinition();
        case 'interface':
          return this.parseInterfaceTypeDefinition();
        case 'union':
          return this.parseUnionTypeDefinition();
        case 'enum':
          return this.parseEnumTypeDefinition();
        case 'input':
          return this.parseInputObjectTypeDefinition();
        case 'directive':
          return this.parseDirectiveDefinition();
      }
      if (hasDescription) {
        throw syntaxError(this._lexer.source, this._lexer.token.start, 'Unexpected description, descriptions are supported only on type definitions.');
      }
      switch (keywordToken.value) {
        case 'query':
        case 'mutation':
        case 'subscription':
          return this.parseOperationDefinition();
        case 'fragment':
          return this.parseFragmentDefinition();
        case 'extend':
          return this.parseTypeSystemExtension();
      }
    }
    throw this.unexpected(keywordToken);
  } // Implements the parsing rules in the Operations section.

  /**
   * OperationDefinition :
   *  - SelectionSet
   *  - OperationType Name? VariableDefinitions? Directives? SelectionSet
   */

  parseOperationDefinition() {
    const start = this._lexer.token;
    if (this.peek(TokenKind.BRACE_L)) {
      return this.node(start, {
        kind: Kind.OPERATION_DEFINITION,
        operation: OperationTypeNode.QUERY,
        name: undefined,
        variableDefinitions: [],
        directives: [],
        selectionSet: this.parseSelectionSet()
      });
    }
    const operation = this.parseOperationType();
    let name;
    if (this.peek(TokenKind.NAME)) {
      name = this.parseName();
    }
    return this.node(start, {
      kind: Kind.OPERATION_DEFINITION,
      operation,
      name,
      variableDefinitions: this.parseVariableDefinitions(),
      directives: this.parseDirectives(false),
      selectionSet: this.parseSelectionSet()
    });
  }
  /**
   * OperationType : one of query mutation subscription
   */

  parseOperationType() {
    const operationToken = this.expectToken(TokenKind.NAME);
    switch (operationToken.value) {
      case 'query':
        return OperationTypeNode.QUERY;
      case 'mutation':
        return OperationTypeNode.MUTATION;
      case 'subscription':
        return OperationTypeNode.SUBSCRIPTION;
    }
    throw this.unexpected(operationToken);
  }
  /**
   * VariableDefinitions : ( VariableDefinition+ )
   */

  parseVariableDefinitions() {
    return this.optionalMany(TokenKind.PAREN_L, this.parseVariableDefinition, TokenKind.PAREN_R);
  }
  /**
   * VariableDefinition : Variable : Type DefaultValue? Directives[Const]?
   */

  parseVariableDefinition() {
    return this.node(this._lexer.token, {
      kind: Kind.VARIABLE_DEFINITION,
      variable: this.parseVariable(),
      type: (this.expectToken(TokenKind.COLON), this.parseTypeReference()),
      defaultValue: this.expectOptionalToken(TokenKind.EQUALS) ? this.parseConstValueLiteral() : undefined,
      directives: this.parseConstDirectives()
    });
  }
  /**
   * Variable : $ Name
   */

  parseVariable() {
    const start = this._lexer.token;
    this.expectToken(TokenKind.DOLLAR);
    return this.node(start, {
      kind: Kind.VARIABLE,
      name: this.parseName()
    });
  }
  /**
   * ```
   * SelectionSet : { Selection+ }
   * ```
   */

  parseSelectionSet() {
    return this.node(this._lexer.token, {
      kind: Kind.SELECTION_SET,
      selections: this.many(TokenKind.BRACE_L, this.parseSelection, TokenKind.BRACE_R)
    });
  }
  /**
   * Selection :
   *   - Field
   *   - FragmentSpread
   *   - InlineFragment
   */

  parseSelection() {
    return this.peek(TokenKind.SPREAD) ? this.parseFragment() : this.parseField();
  }
  /**
   * Field : Alias? Name Arguments? Directives? SelectionSet?
   *
   * Alias : Name :
   */

  parseField() {
    const start = this._lexer.token;
    const nameOrAlias = this.parseName();
    let alias;
    let name;
    if (this.expectOptionalToken(TokenKind.COLON)) {
      alias = nameOrAlias;
      name = this.parseName();
    } else {
      name = nameOrAlias;
    }
    return this.node(start, {
      kind: Kind.FIELD,
      alias,
      name,
      arguments: this.parseArguments(false),
      directives: this.parseDirectives(false),
      selectionSet: this.peek(TokenKind.BRACE_L) ? this.parseSelectionSet() : undefined
    });
  }
  /**
   * Arguments[Const] : ( Argument[?Const]+ )
   */

  parseArguments(isConst) {
    const item = isConst ? this.parseConstArgument : this.parseArgument;
    return this.optionalMany(TokenKind.PAREN_L, item, TokenKind.PAREN_R);
  }
  /**
   * Argument[Const] : Name : Value[?Const]
   */

  parseArgument(isConst = false) {
    const start = this._lexer.token;
    const name = this.parseName();
    this.expectToken(TokenKind.COLON);
    return this.node(start, {
      kind: Kind.ARGUMENT,
      name,
      value: this.parseValueLiteral(isConst)
    });
  }
  parseConstArgument() {
    return this.parseArgument(true);
  } // Implements the parsing rules in the Fragments section.

  /**
   * Corresponds to both FragmentSpread and InlineFragment in the spec.
   *
   * FragmentSpread : ... FragmentName Directives?
   *
   * InlineFragment : ... TypeCondition? Directives? SelectionSet
   */

  parseFragment() {
    const start = this._lexer.token;
    this.expectToken(TokenKind.SPREAD);
    const hasTypeCondition = this.expectOptionalKeyword('on');
    if (!hasTypeCondition && this.peek(TokenKind.NAME)) {
      return this.node(start, {
        kind: Kind.FRAGMENT_SPREAD,
        name: this.parseFragmentName(),
        directives: this.parseDirectives(false)
      });
    }
    return this.node(start, {
      kind: Kind.INLINE_FRAGMENT,
      typeCondition: hasTypeCondition ? this.parseNamedType() : undefined,
      directives: this.parseDirectives(false),
      selectionSet: this.parseSelectionSet()
    });
  }
  /**
   * FragmentDefinition :
   *   - fragment FragmentName on TypeCondition Directives? SelectionSet
   *
   * TypeCondition : NamedType
   */

  parseFragmentDefinition() {
    const start = this._lexer.token;
    this.expectKeyword('fragment'); // Legacy support for defining variables within fragments changes
    // the grammar of FragmentDefinition:
    //   - fragment FragmentName VariableDefinitions? on TypeCondition Directives? SelectionSet

    if (this._options.allowLegacyFragmentVariables === true) {
      return this.node(start, {
        kind: Kind.FRAGMENT_DEFINITION,
        name: this.parseFragmentName(),
        variableDefinitions: this.parseVariableDefinitions(),
        typeCondition: (this.expectKeyword('on'), this.parseNamedType()),
        directives: this.parseDirectives(false),
        selectionSet: this.parseSelectionSet()
      });
    }
    return this.node(start, {
      kind: Kind.FRAGMENT_DEFINITION,
      name: this.parseFragmentName(),
      typeCondition: (this.expectKeyword('on'), this.parseNamedType()),
      directives: this.parseDirectives(false),
      selectionSet: this.parseSelectionSet()
    });
  }
  /**
   * FragmentName : Name but not `on`
   */

  parseFragmentName() {
    if (this._lexer.token.value === 'on') {
      throw this.unexpected();
    }
    return this.parseName();
  } // Implements the parsing rules in the Values section.

  /**
   * Value[Const] :
   *   - [~Const] Variable
   *   - IntValue
   *   - FloatValue
   *   - StringValue
   *   - BooleanValue
   *   - NullValue
   *   - EnumValue
   *   - ListValue[?Const]
   *   - ObjectValue[?Const]
   *
   * BooleanValue : one of `true` `false`
   *
   * NullValue : `null`
   *
   * EnumValue : Name but not `true`, `false` or `null`
   */

  parseValueLiteral(isConst) {
    const token = this._lexer.token;
    switch (token.kind) {
      case TokenKind.BRACKET_L:
        return this.parseList(isConst);
      case TokenKind.BRACE_L:
        return this.parseObject(isConst);
      case TokenKind.INT:
        this.advanceLexer();
        return this.node(token, {
          kind: Kind.INT,
          value: token.value
        });
      case TokenKind.FLOAT:
        this.advanceLexer();
        return this.node(token, {
          kind: Kind.FLOAT,
          value: token.value
        });
      case TokenKind.STRING:
      case TokenKind.BLOCK_STRING:
        return this.parseStringLiteral();
      case TokenKind.NAME:
        this.advanceLexer();
        switch (token.value) {
          case 'true':
            return this.node(token, {
              kind: Kind.BOOLEAN,
              value: true
            });
          case 'false':
            return this.node(token, {
              kind: Kind.BOOLEAN,
              value: false
            });
          case 'null':
            return this.node(token, {
              kind: Kind.NULL
            });
          default:
            return this.node(token, {
              kind: Kind.ENUM,
              value: token.value
            });
        }
      case TokenKind.DOLLAR:
        if (isConst) {
          this.expectToken(TokenKind.DOLLAR);
          if (this._lexer.token.kind === TokenKind.NAME) {
            const varName = this._lexer.token.value;
            throw syntaxError(this._lexer.source, token.start, `Unexpected variable "$${varName}" in constant value.`);
          } else {
            throw this.unexpected(token);
          }
        }
        return this.parseVariable();
      default:
        throw this.unexpected();
    }
  }
  parseConstValueLiteral() {
    return this.parseValueLiteral(true);
  }
  parseStringLiteral() {
    const token = this._lexer.token;
    this.advanceLexer();
    return this.node(token, {
      kind: Kind.STRING,
      value: token.value,
      block: token.kind === TokenKind.BLOCK_STRING
    });
  }
  /**
   * ListValue[Const] :
   *   - [ ]
   *   - [ Value[?Const]+ ]
   */

  parseList(isConst) {
    const item = () => this.parseValueLiteral(isConst);
    return this.node(this._lexer.token, {
      kind: Kind.LIST,
      values: this.any(TokenKind.BRACKET_L, item, TokenKind.BRACKET_R)
    });
  }
  /**
   * ```
   * ObjectValue[Const] :
   *   - { }
   *   - { ObjectField[?Const]+ }
   * ```
   */

  parseObject(isConst) {
    const item = () => this.parseObjectField(isConst);
    return this.node(this._lexer.token, {
      kind: Kind.OBJECT,
      fields: this.any(TokenKind.BRACE_L, item, TokenKind.BRACE_R)
    });
  }
  /**
   * ObjectField[Const] : Name : Value[?Const]
   */

  parseObjectField(isConst) {
    const start = this._lexer.token;
    const name = this.parseName();
    this.expectToken(TokenKind.COLON);
    return this.node(start, {
      kind: Kind.OBJECT_FIELD,
      name,
      value: this.parseValueLiteral(isConst)
    });
  } // Implements the parsing rules in the Directives section.

  /**
   * Directives[Const] : Directive[?Const]+
   */

  parseDirectives(isConst) {
    const directives = [];
    while (this.peek(TokenKind.AT)) {
      directives.push(this.parseDirective(isConst));
    }
    return directives;
  }
  parseConstDirectives() {
    return this.parseDirectives(true);
  }
  /**
   * ```
   * Directive[Const] : @ Name Arguments[?Const]?
   * ```
   */

  parseDirective(isConst) {
    const start = this._lexer.token;
    this.expectToken(TokenKind.AT);
    return this.node(start, {
      kind: Kind.DIRECTIVE,
      name: this.parseName(),
      arguments: this.parseArguments(isConst)
    });
  } // Implements the parsing rules in the Types section.

  /**
   * Type :
   *   - NamedType
   *   - ListType
   *   - NonNullType
   */

  parseTypeReference() {
    const start = this._lexer.token;
    let type;
    if (this.expectOptionalToken(TokenKind.BRACKET_L)) {
      const innerType = this.parseTypeReference();
      this.expectToken(TokenKind.BRACKET_R);
      type = this.node(start, {
        kind: Kind.LIST_TYPE,
        type: innerType
      });
    } else {
      type = this.parseNamedType();
    }
    if (this.expectOptionalToken(TokenKind.BANG)) {
      return this.node(start, {
        kind: Kind.NON_NULL_TYPE,
        type
      });
    }
    return type;
  }
  /**
   * NamedType : Name
   */

  parseNamedType() {
    return this.node(this._lexer.token, {
      kind: Kind.NAMED_TYPE,
      name: this.parseName()
    });
  } // Implements the parsing rules in the Type Definition section.

  peekDescription() {
    return this.peek(TokenKind.STRING) || this.peek(TokenKind.BLOCK_STRING);
  }
  /**
   * Description : StringValue
   */

  parseDescription() {
    if (this.peekDescription()) {
      return this.parseStringLiteral();
    }
  }
  /**
   * ```
   * SchemaDefinition : Description? schema Directives[Const]? { OperationTypeDefinition+ }
   * ```
   */

  parseSchemaDefinition() {
    const start = this._lexer.token;
    const description = this.parseDescription();
    this.expectKeyword('schema');
    const directives = this.parseConstDirectives();
    const operationTypes = this.many(TokenKind.BRACE_L, this.parseOperationTypeDefinition, TokenKind.BRACE_R);
    return this.node(start, {
      kind: Kind.SCHEMA_DEFINITION,
      description,
      directives,
      operationTypes
    });
  }
  /**
   * OperationTypeDefinition : OperationType : NamedType
   */

  parseOperationTypeDefinition() {
    const start = this._lexer.token;
    const operation = this.parseOperationType();
    this.expectToken(TokenKind.COLON);
    const type = this.parseNamedType();
    return this.node(start, {
      kind: Kind.OPERATION_TYPE_DEFINITION,
      operation,
      type
    });
  }
  /**
   * ScalarTypeDefinition : Description? scalar Name Directives[Const]?
   */

  parseScalarTypeDefinition() {
    const start = this._lexer.token;
    const description = this.parseDescription();
    this.expectKeyword('scalar');
    const name = this.parseName();
    const directives = this.parseConstDirectives();
    return this.node(start, {
      kind: Kind.SCALAR_TYPE_DEFINITION,
      description,
      name,
      directives
    });
  }
  /**
   * ObjectTypeDefinition :
   *   Description?
   *   type Name ImplementsInterfaces? Directives[Const]? FieldsDefinition?
   */

  parseObjectTypeDefinition() {
    const start = this._lexer.token;
    const description = this.parseDescription();
    this.expectKeyword('type');
    const name = this.parseName();
    const interfaces = this.parseImplementsInterfaces();
    const directives = this.parseConstDirectives();
    const fields = this.parseFieldsDefinition();
    return this.node(start, {
      kind: Kind.OBJECT_TYPE_DEFINITION,
      description,
      name,
      interfaces,
      directives,
      fields
    });
  }
  /**
   * ImplementsInterfaces :
   *   - implements `&`? NamedType
   *   - ImplementsInterfaces & NamedType
   */

  parseImplementsInterfaces() {
    return this.expectOptionalKeyword('implements') ? this.delimitedMany(TokenKind.AMP, this.parseNamedType) : [];
  }
  /**
   * ```
   * FieldsDefinition : { FieldDefinition+ }
   * ```
   */

  parseFieldsDefinition() {
    return this.optionalMany(TokenKind.BRACE_L, this.parseFieldDefinition, TokenKind.BRACE_R);
  }
  /**
   * FieldDefinition :
   *   - Description? Name ArgumentsDefinition? : Type Directives[Const]?
   */

  parseFieldDefinition() {
    const start = this._lexer.token;
    const description = this.parseDescription();
    const name = this.parseName();
    const args = this.parseArgumentDefs();
    this.expectToken(TokenKind.COLON);
    const type = this.parseTypeReference();
    const directives = this.parseConstDirectives();
    return this.node(start, {
      kind: Kind.FIELD_DEFINITION,
      description,
      name,
      arguments: args,
      type,
      directives
    });
  }
  /**
   * ArgumentsDefinition : ( InputValueDefinition+ )
   */

  parseArgumentDefs() {
    return this.optionalMany(TokenKind.PAREN_L, this.parseInputValueDef, TokenKind.PAREN_R);
  }
  /**
   * InputValueDefinition :
   *   - Description? Name : Type DefaultValue? Directives[Const]?
   */

  parseInputValueDef() {
    const start = this._lexer.token;
    const description = this.parseDescription();
    const name = this.parseName();
    this.expectToken(TokenKind.COLON);
    const type = this.parseTypeReference();
    let defaultValue;
    if (this.expectOptionalToken(TokenKind.EQUALS)) {
      defaultValue = this.parseConstValueLiteral();
    }
    const directives = this.parseConstDirectives();
    return this.node(start, {
      kind: Kind.INPUT_VALUE_DEFINITION,
      description,
      name,
      type,
      defaultValue,
      directives
    });
  }
  /**
   * InterfaceTypeDefinition :
   *   - Description? interface Name Directives[Const]? FieldsDefinition?
   */

  parseInterfaceTypeDefinition() {
    const start = this._lexer.token;
    const description = this.parseDescription();
    this.expectKeyword('interface');
    const name = this.parseName();
    const interfaces = this.parseImplementsInterfaces();
    const directives = this.parseConstDirectives();
    const fields = this.parseFieldsDefinition();
    return this.node(start, {
      kind: Kind.INTERFACE_TYPE_DEFINITION,
      description,
      name,
      interfaces,
      directives,
      fields
    });
  }
  /**
   * UnionTypeDefinition :
   *   - Description? union Name Directives[Const]? UnionMemberTypes?
   */

  parseUnionTypeDefinition() {
    const start = this._lexer.token;
    const description = this.parseDescription();
    this.expectKeyword('union');
    const name = this.parseName();
    const directives = this.parseConstDirectives();
    const types = this.parseUnionMemberTypes();
    return this.node(start, {
      kind: Kind.UNION_TYPE_DEFINITION,
      description,
      name,
      directives,
      types
    });
  }
  /**
   * UnionMemberTypes :
   *   - = `|`? NamedType
   *   - UnionMemberTypes | NamedType
   */

  parseUnionMemberTypes() {
    return this.expectOptionalToken(TokenKind.EQUALS) ? this.delimitedMany(TokenKind.PIPE, this.parseNamedType) : [];
  }
  /**
   * EnumTypeDefinition :
   *   - Description? enum Name Directives[Const]? EnumValuesDefinition?
   */

  parseEnumTypeDefinition() {
    const start = this._lexer.token;
    const description = this.parseDescription();
    this.expectKeyword('enum');
    const name = this.parseName();
    const directives = this.parseConstDirectives();
    const values = this.parseEnumValuesDefinition();
    return this.node(start, {
      kind: Kind.ENUM_TYPE_DEFINITION,
      description,
      name,
      directives,
      values
    });
  }
  /**
   * ```
   * EnumValuesDefinition : { EnumValueDefinition+ }
   * ```
   */

  parseEnumValuesDefinition() {
    return this.optionalMany(TokenKind.BRACE_L, this.parseEnumValueDefinition, TokenKind.BRACE_R);
  }
  /**
   * EnumValueDefinition : Description? EnumValue Directives[Const]?
   */

  parseEnumValueDefinition() {
    const start = this._lexer.token;
    const description = this.parseDescription();
    const name = this.parseEnumValueName();
    const directives = this.parseConstDirectives();
    return this.node(start, {
      kind: Kind.ENUM_VALUE_DEFINITION,
      description,
      name,
      directives
    });
  }
  /**
   * EnumValue : Name but not `true`, `false` or `null`
   */

  parseEnumValueName() {
    if (this._lexer.token.value === 'true' || this._lexer.token.value === 'false' || this._lexer.token.value === 'null') {
      throw syntaxError(this._lexer.source, this._lexer.token.start, `${getTokenDesc(this._lexer.token)} is reserved and cannot be used for an enum value.`);
    }
    return this.parseName();
  }
  /**
   * InputObjectTypeDefinition :
   *   - Description? input Name Directives[Const]? InputFieldsDefinition?
   */

  parseInputObjectTypeDefinition() {
    const start = this._lexer.token;
    const description = this.parseDescription();
    this.expectKeyword('input');
    const name = this.parseName();
    const directives = this.parseConstDirectives();
    const fields = this.parseInputFieldsDefinition();
    return this.node(start, {
      kind: Kind.INPUT_OBJECT_TYPE_DEFINITION,
      description,
      name,
      directives,
      fields
    });
  }
  /**
   * ```
   * InputFieldsDefinition : { InputValueDefinition+ }
   * ```
   */

  parseInputFieldsDefinition() {
    return this.optionalMany(TokenKind.BRACE_L, this.parseInputValueDef, TokenKind.BRACE_R);
  }
  /**
   * TypeSystemExtension :
   *   - SchemaExtension
   *   - TypeExtension
   *
   * TypeExtension :
   *   - ScalarTypeExtension
   *   - ObjectTypeExtension
   *   - InterfaceTypeExtension
   *   - UnionTypeExtension
   *   - EnumTypeExtension
   *   - InputObjectTypeDefinition
   */

  parseTypeSystemExtension() {
    const keywordToken = this._lexer.lookahead();
    if (keywordToken.kind === TokenKind.NAME) {
      switch (keywordToken.value) {
        case 'schema':
          return this.parseSchemaExtension();
        case 'scalar':
          return this.parseScalarTypeExtension();
        case 'type':
          return this.parseObjectTypeExtension();
        case 'interface':
          return this.parseInterfaceTypeExtension();
        case 'union':
          return this.parseUnionTypeExtension();
        case 'enum':
          return this.parseEnumTypeExtension();
        case 'input':
          return this.parseInputObjectTypeExtension();
      }
    }
    throw this.unexpected(keywordToken);
  }
  /**
   * ```
   * SchemaExtension :
   *  - extend schema Directives[Const]? { OperationTypeDefinition+ }
   *  - extend schema Directives[Const]
   * ```
   */

  parseSchemaExtension() {
    const start = this._lexer.token;
    this.expectKeyword('extend');
    this.expectKeyword('schema');
    const directives = this.parseConstDirectives();
    const operationTypes = this.optionalMany(TokenKind.BRACE_L, this.parseOperationTypeDefinition, TokenKind.BRACE_R);
    if (directives.length === 0 && operationTypes.length === 0) {
      throw this.unexpected();
    }
    return this.node(start, {
      kind: Kind.SCHEMA_EXTENSION,
      directives,
      operationTypes
    });
  }
  /**
   * ScalarTypeExtension :
   *   - extend scalar Name Directives[Const]
   */

  parseScalarTypeExtension() {
    const start = this._lexer.token;
    this.expectKeyword('extend');
    this.expectKeyword('scalar');
    const name = this.parseName();
    const directives = this.parseConstDirectives();
    if (directives.length === 0) {
      throw this.unexpected();
    }
    return this.node(start, {
      kind: Kind.SCALAR_TYPE_EXTENSION,
      name,
      directives
    });
  }
  /**
   * ObjectTypeExtension :
   *  - extend type Name ImplementsInterfaces? Directives[Const]? FieldsDefinition
   *  - extend type Name ImplementsInterfaces? Directives[Const]
   *  - extend type Name ImplementsInterfaces
   */

  parseObjectTypeExtension() {
    const start = this._lexer.token;
    this.expectKeyword('extend');
    this.expectKeyword('type');
    const name = this.parseName();
    const interfaces = this.parseImplementsInterfaces();
    const directives = this.parseConstDirectives();
    const fields = this.parseFieldsDefinition();
    if (interfaces.length === 0 && directives.length === 0 && fields.length === 0) {
      throw this.unexpected();
    }
    return this.node(start, {
      kind: Kind.OBJECT_TYPE_EXTENSION,
      name,
      interfaces,
      directives,
      fields
    });
  }
  /**
   * InterfaceTypeExtension :
   *  - extend interface Name ImplementsInterfaces? Directives[Const]? FieldsDefinition
   *  - extend interface Name ImplementsInterfaces? Directives[Const]
   *  - extend interface Name ImplementsInterfaces
   */

  parseInterfaceTypeExtension() {
    const start = this._lexer.token;
    this.expectKeyword('extend');
    this.expectKeyword('interface');
    const name = this.parseName();
    const interfaces = this.parseImplementsInterfaces();
    const directives = this.parseConstDirectives();
    const fields = this.parseFieldsDefinition();
    if (interfaces.length === 0 && directives.length === 0 && fields.length === 0) {
      throw this.unexpected();
    }
    return this.node(start, {
      kind: Kind.INTERFACE_TYPE_EXTENSION,
      name,
      interfaces,
      directives,
      fields
    });
  }
  /**
   * UnionTypeExtension :
   *   - extend union Name Directives[Const]? UnionMemberTypes
   *   - extend union Name Directives[Const]
   */

  parseUnionTypeExtension() {
    const start = this._lexer.token;
    this.expectKeyword('extend');
    this.expectKeyword('union');
    const name = this.parseName();
    const directives = this.parseConstDirectives();
    const types = this.parseUnionMemberTypes();
    if (directives.length === 0 && types.length === 0) {
      throw this.unexpected();
    }
    return this.node(start, {
      kind: Kind.UNION_TYPE_EXTENSION,
      name,
      directives,
      types
    });
  }
  /**
   * EnumTypeExtension :
   *   - extend enum Name Directives[Const]? EnumValuesDefinition
   *   - extend enum Name Directives[Const]
   */

  parseEnumTypeExtension() {
    const start = this._lexer.token;
    this.expectKeyword('extend');
    this.expectKeyword('enum');
    const name = this.parseName();
    const directives = this.parseConstDirectives();
    const values = this.parseEnumValuesDefinition();
    if (directives.length === 0 && values.length === 0) {
      throw this.unexpected();
    }
    return this.node(start, {
      kind: Kind.ENUM_TYPE_EXTENSION,
      name,
      directives,
      values
    });
  }
  /**
   * InputObjectTypeExtension :
   *   - extend input Name Directives[Const]? InputFieldsDefinition
   *   - extend input Name Directives[Const]
   */

  parseInputObjectTypeExtension() {
    const start = this._lexer.token;
    this.expectKeyword('extend');
    this.expectKeyword('input');
    const name = this.parseName();
    const directives = this.parseConstDirectives();
    const fields = this.parseInputFieldsDefinition();
    if (directives.length === 0 && fields.length === 0) {
      throw this.unexpected();
    }
    return this.node(start, {
      kind: Kind.INPUT_OBJECT_TYPE_EXTENSION,
      name,
      directives,
      fields
    });
  }
  /**
   * ```
   * DirectiveDefinition :
   *   - Description? directive @ Name ArgumentsDefinition? `repeatable`? on DirectiveLocations
   * ```
   */

  parseDirectiveDefinition() {
    const start = this._lexer.token;
    const description = this.parseDescription();
    this.expectKeyword('directive');
    this.expectToken(TokenKind.AT);
    const name = this.parseName();
    const args = this.parseArgumentDefs();
    const repeatable = this.expectOptionalKeyword('repeatable');
    this.expectKeyword('on');
    const locations = this.parseDirectiveLocations();
    return this.node(start, {
      kind: Kind.DIRECTIVE_DEFINITION,
      description,
      name,
      arguments: args,
      repeatable,
      locations
    });
  }
  /**
   * DirectiveLocations :
   *   - `|`? DirectiveLocation
   *   - DirectiveLocations | DirectiveLocation
   */

  parseDirectiveLocations() {
    return this.delimitedMany(TokenKind.PIPE, this.parseDirectiveLocation);
  }
  /*
   * DirectiveLocation :
   *   - ExecutableDirectiveLocation
   *   - TypeSystemDirectiveLocation
   *
   * ExecutableDirectiveLocation : one of
   *   `QUERY`
   *   `MUTATION`
   *   `SUBSCRIPTION`
   *   `FIELD`
   *   `FRAGMENT_DEFINITION`
   *   `FRAGMENT_SPREAD`
   *   `INLINE_FRAGMENT`
   *
   * TypeSystemDirectiveLocation : one of
   *   `SCHEMA`
   *   `SCALAR`
   *   `OBJECT`
   *   `FIELD_DEFINITION`
   *   `ARGUMENT_DEFINITION`
   *   `INTERFACE`
   *   `UNION`
   *   `ENUM`
   *   `ENUM_VALUE`
   *   `INPUT_OBJECT`
   *   `INPUT_FIELD_DEFINITION`
   */

  parseDirectiveLocation() {
    const start = this._lexer.token;
    const name = this.parseName();
    if (Object.prototype.hasOwnProperty.call(DirectiveLocation, name.value)) {
      return name;
    }
    throw this.unexpected(start);
  } // Core parsing utility functions

  /**
   * Returns a node that, if configured to do so, sets a "loc" field as a
   * location object, used to identify the place in the source that created a
   * given parsed object.
   */

  node(startToken, node) {
    if (this._options.noLocation !== true) {
      node.loc = new Location(startToken, this._lexer.lastToken, this._lexer.source);
    }
    return node;
  }
  /**
   * Determines if the next token is of a given kind
   */

  peek(kind) {
    return this._lexer.token.kind === kind;
  }
  /**
   * If the next token is of the given kind, return that token after advancing the lexer.
   * Otherwise, do not change the parser state and throw an error.
   */

  expectToken(kind) {
    const token = this._lexer.token;
    if (token.kind === kind) {
      this.advanceLexer();
      return token;
    }
    throw syntaxError(this._lexer.source, token.start, `Expected ${getTokenKindDesc(kind)}, found ${getTokenDesc(token)}.`);
  }
  /**
   * If the next token is of the given kind, return "true" after advancing the lexer.
   * Otherwise, do not change the parser state and return "false".
   */

  expectOptionalToken(kind) {
    const token = this._lexer.token;
    if (token.kind === kind) {
      this.advanceLexer();
      return true;
    }
    return false;
  }
  /**
   * If the next token is a given keyword, advance the lexer.
   * Otherwise, do not change the parser state and throw an error.
   */

  expectKeyword(value) {
    const token = this._lexer.token;
    if (token.kind === TokenKind.NAME && token.value === value) {
      this.advanceLexer();
    } else {
      throw syntaxError(this._lexer.source, token.start, `Expected "${value}", found ${getTokenDesc(token)}.`);
    }
  }
  /**
   * If the next token is a given keyword, return "true" after advancing the lexer.
   * Otherwise, do not change the parser state and return "false".
   */

  expectOptionalKeyword(value) {
    const token = this._lexer.token;
    if (token.kind === TokenKind.NAME && token.value === value) {
      this.advanceLexer();
      return true;
    }
    return false;
  }
  /**
   * Helper function for creating an error when an unexpected lexed token is encountered.
   */

  unexpected(atToken) {
    const token = atToken !== null && atToken !== void 0 ? atToken : this._lexer.token;
    return syntaxError(this._lexer.source, token.start, `Unexpected ${getTokenDesc(token)}.`);
  }
  /**
   * Returns a possibly empty list of parse nodes, determined by the parseFn.
   * This list begins with a lex token of openKind and ends with a lex token of closeKind.
   * Advances the parser to the next lex token after the closing token.
   */

  any(openKind, parseFn, closeKind) {
    this.expectToken(openKind);
    const nodes = [];
    while (!this.expectOptionalToken(closeKind)) {
      nodes.push(parseFn.call(this));
    }
    return nodes;
  }
  /**
   * Returns a list of parse nodes, determined by the parseFn.
   * It can be empty only if open token is missing otherwise it will always return non-empty list
   * that begins with a lex token of openKind and ends with a lex token of closeKind.
   * Advances the parser to the next lex token after the closing token.
   */

  optionalMany(openKind, parseFn, closeKind) {
    if (this.expectOptionalToken(openKind)) {
      const nodes = [];
      do {
        nodes.push(parseFn.call(this));
      } while (!this.expectOptionalToken(closeKind));
      return nodes;
    }
    return [];
  }
  /**
   * Returns a non-empty list of parse nodes, determined by the parseFn.
   * This list begins with a lex token of openKind and ends with a lex token of closeKind.
   * Advances the parser to the next lex token after the closing token.
   */

  many(openKind, parseFn, closeKind) {
    this.expectToken(openKind);
    const nodes = [];
    do {
      nodes.push(parseFn.call(this));
    } while (!this.expectOptionalToken(closeKind));
    return nodes;
  }
  /**
   * Returns a non-empty list of parse nodes, determined by the parseFn.
   * This list may begin with a lex token of delimiterKind followed by items separated by lex tokens of tokenKind.
   * Advances the parser to the next lex token after last item in the list.
   */

  delimitedMany(delimiterKind, parseFn) {
    this.expectOptionalToken(delimiterKind);
    const nodes = [];
    do {
      nodes.push(parseFn.call(this));
    } while (this.expectOptionalToken(delimiterKind));
    return nodes;
  }
  advanceLexer() {
    const {
      maxTokens
    } = this._options;
    const token = this._lexer.advance();
    if (maxTokens !== undefined && token.kind !== TokenKind.EOF) {
      ++this._tokenCounter;
      if (this._tokenCounter > maxTokens) {
        throw syntaxError(this._lexer.source, token.start, `Document contains more that ${maxTokens} tokens. Parsing aborted.`);
      }
    }
  }
}
/**
 * A helper function to describe a token as a string for debugging.
 */

function getTokenDesc(token) {
  const value = token.value;
  return getTokenKindDesc(token.kind) + (value != null ? ` "${value}"` : '');
}
/**
 * A helper function to describe a token kind as a string for debugging.
 */

function getTokenKindDesc(kind) {
  return isPunctuatorTokenKind(kind) ? `"${kind}"` : kind;
}

var docCache = new Map();
var fragmentSourceMap = new Map();
var printFragmentWarnings = true;
var experimentalFragmentVariables = false;
function normalize(string) {
  return string.replace(/[\s,]+/g, ' ').trim();
}
function cacheKeyFromLoc(loc) {
  return normalize(loc.source.body.substring(loc.start, loc.end));
}
function processFragments(ast) {
  var seenKeys = new Set();
  var definitions = [];
  ast.definitions.forEach(function (fragmentDefinition) {
    if (fragmentDefinition.kind === 'FragmentDefinition') {
      var fragmentName = fragmentDefinition.name.value;
      var sourceKey = cacheKeyFromLoc(fragmentDefinition.loc);
      var sourceKeySet = fragmentSourceMap.get(fragmentName);
      if (sourceKeySet && !sourceKeySet.has(sourceKey)) {
        if (printFragmentWarnings) {
          console.warn("Warning: fragment with name " + fragmentName + " already exists.\n" + "graphql-tag enforces all fragment names across your application to be unique; read more about\n" + "this in the docs: http://dev.apollodata.com/core/fragments.html#unique-names");
        }
      } else if (!sourceKeySet) {
        fragmentSourceMap.set(fragmentName, sourceKeySet = new Set());
      }
      sourceKeySet.add(sourceKey);
      if (!seenKeys.has(sourceKey)) {
        seenKeys.add(sourceKey);
        definitions.push(fragmentDefinition);
      }
    } else {
      definitions.push(fragmentDefinition);
    }
  });
  return __assign(__assign({}, ast), {
    definitions: definitions
  });
}
function stripLoc(doc) {
  var workSet = new Set(doc.definitions);
  workSet.forEach(function (node) {
    if (node.loc) delete node.loc;
    Object.keys(node).forEach(function (key) {
      var value = node[key];
      if (value && typeof value === 'object') {
        workSet.add(value);
      }
    });
  });
  var loc = doc.loc;
  if (loc) {
    delete loc.startToken;
    delete loc.endToken;
  }
  return doc;
}
function parseDocument(source) {
  var cacheKey = normalize(source);
  if (!docCache.has(cacheKey)) {
    var parsed = parse(source, {
      experimentalFragmentVariables: experimentalFragmentVariables,
      allowLegacyFragmentVariables: experimentalFragmentVariables
    });
    if (!parsed || parsed.kind !== 'Document') {
      throw new Error('Not a valid GraphQL document.');
    }
    docCache.set(cacheKey, stripLoc(processFragments(parsed)));
  }
  return docCache.get(cacheKey);
}
function gql(literals) {
  var args = [];
  for (var _i = 1; _i < arguments.length; _i++) {
    args[_i - 1] = arguments[_i];
  }
  if (typeof literals === 'string') {
    literals = [literals];
  }
  var result = literals[0];
  args.forEach(function (arg, i) {
    if (arg && arg.kind === 'Document') {
      result += arg.loc.source.body;
    } else {
      result += arg;
    }
    result += literals[i + 1];
  });
  return parseDocument(result);
}
function resetCaches() {
  docCache.clear();
  fragmentSourceMap.clear();
}
function disableFragmentWarnings() {
  printFragmentWarnings = false;
}
function enableExperimentalFragmentVariables() {
  experimentalFragmentVariables = true;
}
function disableExperimentalFragmentVariables() {
  experimentalFragmentVariables = false;
}
var extras = {
  gql: gql,
  resetCaches: resetCaches,
  disableFragmentWarnings: disableFragmentWarnings,
  enableExperimentalFragmentVariables: enableExperimentalFragmentVariables,
  disableExperimentalFragmentVariables: disableExperimentalFragmentVariables
};
(function (gql_1) {
  gql_1.gql = extras.gql, gql_1.resetCaches = extras.resetCaches, gql_1.disableFragmentWarnings = extras.disableFragmentWarnings, gql_1.enableExperimentalFragmentVariables = extras.enableExperimentalFragmentVariables, gql_1.disableExperimentalFragmentVariables = extras.disableExperimentalFragmentVariables;
})(gql || (gql = {}));
gql["default"] = gql;

const JobsInquiryHiringsQuery = gql`
  query JobsInquiryHirings {
    currentJobsInquiry {
      id
      stale
      hiringsForReview {
        id
      }
    }
  }
`;
var JobsInquiryHiringsQuery$1 = JobsInquiryHiringsQuery;

var ExternalLinkIcon = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"width":"20","height":"20","fill":"none","xmlns":"http://www.w3.org/2000/svg"}},[_c('path',{attrs:{"d":"M17.5 8.125a.625.625 0 11-1.25 0v-3.49l-5.182 5.182a.625.625 0 01-.884-.884l5.182-5.183h-3.491a.625.625 0 110-1.25h5a.625.625 0 01.625.625v5zM14.375 10a.624.624 0 00-.625.625v5.625h-10v-10h5.625a.625.625 0 000-1.25H3.75A1.25 1.25 0 002.5 6.25v10a1.25 1.25 0 001.25 1.25h10A1.25 1.25 0 0015 16.25v-5.625a.624.624 0 00-.625-.625z","fill":"currentColor"}})]) } };

const routes = {
  HOME: {
    path: '/',
    identifier: '/'
  },
  JOBS: {
    path: '/jobs',
    identifier: '/jobs'
  },
  JOBS_PROFILE: {
    path: '/jobs/profile',
    identifier: 'jobs/profile'
  },
  JOBS_MY_MATCHES: {
    path: '/jobs/my-matches',
    identifier: '/jobs/my-matches'
  },
  WAR_ROOM: {
    path: '/war-room/posts',
    identifier: '/war-room/posts'
  },
  PRODUCTS: {
    path: '/war-room/products',
    identifier: '/war-room/products'
  },
  LOGIN_EXTERNAL_SIGN_IN: {
    path: '/login-external?dynamic_modal=sign-in',
    identifier: '/login-external'
  },
  LOGIN_EXTERNAL_SIGN_UP: {
    path: '/login-external?dynamic_modal=sign-up',
    identifier: '/login-external'
  },
  SALARIES: {
    path: '/salaries',
    identifier: '/salaries'
  },
  PORTFOLIO: {
    path: '/p',
    identifier: '/p'
  },
  PROFILE_SETTINGS: {
    path: '/profile/settings',
    identifier: '/profile/settings'
  },
  PROFILE_REFERRAL: {
    path: '/profile/referral',
    identifier: '/profile/referral'
  }
};
const pages = {
  ONBOARDING: {
    path: '/simplified-onboarding/',
    identifier: '/simplified-onboarding'
  },
  SALES_GPT: {
    path: '/salesgpt',
    identifier: 'salesgpt'
  },
  ACADEMY: {
    path: '/blog/tag/academy/',
    identifier: '/blog/tag/academy/'
  },
  TECH_SALES_U: {
    path: '/lp/techsalesu/',
    identifier: '/lp/techsalesu'
  },
  TALENT_LANDING: {
    path: '/talent',
    identifier: '/talent'
  },
  EXEC_SEARCH_LANDING: {
    path: '/exec-search',
    identifier: '/exec-search'
  }
};

const PORTFOLIO_REFETCH_EVENT = 'portfolio:refetch';
const BRAVADO_NAV_REDIRECT = 'bravado-nav:redirect';
const GlobalEmitter = new Vue();
const emitPortfolioRefetchEvent = slug => {
  GlobalEmitter.$emit(PORTFOLIO_REFETCH_EVENT, {
    slug
  });
};

// type can be route or page
const emitRedirectEvent = ({
  path,
  sameOrigin = true,
  newTab = false,
  replacePage = false
}) => {
  GlobalEmitter.$emit(BRAVADO_NAV_REDIRECT, {
    path,
    sameOrigin,
    newTab,
    replacePage
  });
};

var ChevronDownIcon = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"width":"14","height":"8","fill":"none","xmlns":"http://www.w3.org/2000/svg"}},[_c('path',{attrs:{"opacity":".4","d":"M12 2L7 7 2 2","stroke":"currentColor","stroke-linecap":"round","stroke-linejoin":"round"}})]) } };

//
var script$c = {
  name: 'MenuButton',
  components: {
    ChevronDownIcon
  },
  props: {
    title: {
      type: String,
      required: false
    },
    isOpenable: {
      type: Boolean,
      default: true
    },
    disableHover: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: 'md'
    }
  },
  computed: {
    sizeStyle() {
      let size = 40;
      if (this.size === 'lg') {
        size = 56;
      }
      return `height: ${size}px;`;
    }
  },
  methods: {
    handleClick(event) {
      event.stopPropagation();
      if (!this.disableHover) {
        return;
      }
      this.$emit('click', event);
    },
    handleHover(event) {
      event.stopPropagation();
      if (this.disableHover) {
        return;
      }
      this.$emit('hover', event);
    },
    handleKeyDown(event) {
      event.stopPropagation();
      if (!['Space', 'Enter'].includes(event.code)) {
        return;
      }
      this.$emit('click', event);
    }
  }
};

function normalizeComponent(template, style, script, scopeId, isFunctionalTemplate, moduleIdentifier /* server only */, shadowMode, createInjector, createInjectorSSR, createInjectorShadow) {
  if (typeof shadowMode !== 'boolean') {
    createInjectorSSR = createInjector;
    createInjector = shadowMode;
    shadowMode = false;
  }
  // Vue.extend constructor export interop.
  const options = typeof script === 'function' ? script.options : script;
  // render functions
  if (template && template.render) {
    options.render = template.render;
    options.staticRenderFns = template.staticRenderFns;
    options._compiled = true;
    // functional template
    if (isFunctionalTemplate) {
      options.functional = true;
    }
  }
  // scopedId
  if (scopeId) {
    options._scopeId = scopeId;
  }
  let hook;
  if (moduleIdentifier) {
    // server build
    hook = function (context) {
      // 2.3 injection
      context = context ||
      // cached call
      this.$vnode && this.$vnode.ssrContext ||
      // stateful
      this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext; // functional
      // 2.2 with runInNewContext: true
      if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
        context = __VUE_SSR_CONTEXT__;
      }
      // inject component styles
      if (style) {
        style.call(this, createInjectorSSR(context));
      }
      // register component module identifier for async chunk inference
      if (context && context._registeredComponents) {
        context._registeredComponents.add(moduleIdentifier);
      }
    };
    // used by ssr in case component is cached and beforeCreate
    // never gets called
    options._ssrRegister = hook;
  } else if (style) {
    hook = shadowMode ? function (context) {
      style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot));
    } : function (context) {
      style.call(this, createInjector(context));
    };
  }
  if (hook) {
    if (options.functional) {
      // register for functional component in vue file
      const originalRender = options.render;
      options.render = function renderWithStyleInjection(h, context) {
        hook.call(context);
        return originalRender(h, context);
      };
    } else {
      // inject component registration as beforeCreate hook
      const existing = options.beforeCreate;
      options.beforeCreate = existing ? [].concat(existing, hook) : [hook];
    }
  }
  return script;
}

const isOldIE = typeof navigator !== 'undefined' && /msie [6-9]\\b/.test(navigator.userAgent.toLowerCase());
function createInjector(context) {
  return (id, style) => addStyle(id, style);
}
let HEAD;
const styles = {};
function addStyle(id, css) {
  const group = isOldIE ? css.media || 'default' : id;
  const style = styles[group] || (styles[group] = {
    ids: new Set(),
    styles: []
  });
  if (!style.ids.has(id)) {
    style.ids.add(id);
    let code = css.source;
    if (css.map) {
      // https://developer.chrome.com/devtools/docs/javascript-debugging
      // this makes source maps inside style tags work properly in Chrome
      code += '\n/*# sourceURL=' + css.map.sources[0] + ' */';
      // http://stackoverflow.com/a/26603875
      code += '\n/*# sourceMappingURL=data:application/json;base64,' + btoa(unescape(encodeURIComponent(JSON.stringify(css.map)))) + ' */';
    }
    if (!style.element) {
      style.element = document.createElement('style');
      style.element.type = 'text/css';
      if (css.media) style.element.setAttribute('media', css.media);
      if (HEAD === undefined) {
        HEAD = document.head || document.getElementsByTagName('head')[0];
      }
      HEAD.appendChild(style.element);
    }
    if ('styleSheet' in style.element) {
      style.styles.push(code);
      style.element.styleSheet.cssText = style.styles.filter(Boolean).join('\n');
    } else {
      const index = style.ids.size - 1;
      const textNode = document.createTextNode(code);
      const nodes = style.element.childNodes;
      if (nodes[index]) style.element.removeChild(nodes[index]);
      if (nodes.length) style.element.insertBefore(textNode, nodes[index]);else style.element.appendChild(textNode);
    }
  }
}

/* script */
const __vue_script__$c = script$c;

/* template */
var __vue_render__$c = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('button', _vm._b({
    staticClass: "menu-button",
    style: _vm.sizeStyle,
    on: {
      "click": _vm.handleClick,
      "mouseover": _vm.handleHover,
      "keydown": _vm.handleKeyDown
    }
  }, 'button', _vm.$attrs, false), [_vm.title ? _c('p', {
    staticClass: "menu-button__title"
  }, [_vm._v("\n    " + _vm._s(_vm.title) + "\n  ")]) : _vm._e(), _vm._v(" "), _vm._t("default"), _vm._v(" "), _vm.isOpenable ? _c('i', {
    staticClass: "menu-button__icon"
  }, [_c('ChevronDownIcon')], 1) : _vm._e()], 2);
};
var __vue_staticRenderFns__$c = [];

/* style */
const __vue_inject_styles__$c = function (inject) {
  if (!inject) return;
  inject("data-v-52bd884c_0", {
    source: ".menu-button[data-v-52bd884c]{background:var(--transparent);cursor:pointer;border:none;display:flex;align-items:center;justify-content:center;background:0 0;gap:8px;cursor:pointer;padding:8px;border-radius:4px;transition-timing-function:ease-in-out;transition-duration:.2s;transition-property:color,background-color,border-color,box-shadow;outline:2px solid var(--transparent);outline-offset:2px;color:var(--text)}.menu-button[data-v-52bd884c]:focus-visible:not([disabled]){outline-color:var(--blue-60)}@media (hover:hover){.menu-button[data-v-52bd884c]:hover{background:var(--transparent-text-a08)}}.menu-button__title[data-v-52bd884c]{font-weight:700}.menu-button__icon[data-v-52bd884c]{width:16px;height:8px;display:flex;align-items:center;justify-content:center}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$c = "data-v-52bd884c";
/* module identifier */
const __vue_module_identifier__$c = undefined;
/* functional template */
const __vue_is_functional_template__$c = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$d = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$c,
  staticRenderFns: __vue_staticRenderFns__$c
}, __vue_inject_styles__$c, __vue_script__$c, __vue_scope_id__$c, __vue_is_functional_template__$c, __vue_module_identifier__$c, false, createInjector, undefined, undefined);

var index = /*#__PURE__*/Object.freeze({
    __proto__: null,
    'default': __vue_component__$d
});

//
//
//
//

var script$b = {
  name: 'Badge',
  props: {
    count: {
      type: Number,
      required: true
    }
  }
};

/* script */
const __vue_script__$b = script$b;

/* template */
var __vue_render__$b = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "badge"
  }, [_vm._v(_vm._s(_vm.count))]);
};
var __vue_staticRenderFns__$b = [];

/* style */
const __vue_inject_styles__$b = function (inject) {
  if (!inject) return;
  inject("data-v-08bd1f20_0", {
    source: ".badge[data-v-08bd1f20]{padding:4px 8px;background:var(--cyan-light);border-radius:4px;width:fit-content;font-weight:600;font-size:12px;color:var(--black)}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$b = "data-v-08bd1f20";
/* module identifier */
const __vue_module_identifier__$b = undefined;
/* functional template */
const __vue_is_functional_template__$b = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$c = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$b,
  staticRenderFns: __vue_staticRenderFns__$b
}, __vue_inject_styles__$b, __vue_script__$b, __vue_scope_id__$b, __vue_is_functional_template__$b, __vue_module_identifier__$b, false, createInjector, undefined, undefined);
var Badge = __vue_component__$c;

//
var script$a = {
  name: 'Menu',
  components: {
    MenuButton: __vue_component__$d,
    Badge
  },
  props: {
    title: {
      type: String,
      required: false
    },
    notifications: {
      type: Number,
      required: false
    },
    isOpen: {
      type: Boolean,
      required: false
    },
    id: {
      type: String,
      required: false
    },
    isOpenable: {
      type: Boolean,
      required: false,
      default: true
    },
    isActive: {
      type: Boolean,
      required: false,
      default: false
    },
    disableHover: {
      type: Boolean,
      required: false,
      default: false
    },
    buttonSize: {
      type: String,
      default: 'md'
    }
  },
  data() {
    return {
      isOpenInternal: this.isOpen,
      shouldCloseMenu: false
    };
  },
  methods: {
    onClickButton() {
      const isOpen = !this.isOpenInternal;
      this.isOpenInternal = isOpen;
      this.$emit('update:is-open', isOpen);
    },
    onHoverButton() {
      const isOpen = true;
      this.isOpenInternal = isOpen;
      this.$emit('update:is-open', isOpen);
    },
    onMouseOver() {
      if (this.disableHover) return;
      this.shouldCloseMenu = false;
    },
    onMouseLeave() {
      if (this.disableHover) return;
      this.shouldCloseMenu = true;
      setTimeout(() => {
        if (!this.shouldCloseMenu) return;
        const isOpen = false;
        this.isOpenInternal = isOpen;
        this.$emit('update:is-open', isOpen);
      }, 200);
    },
    handleClickOutside(event) {
      event.stopPropagation();
      const targetElement = this.$refs.targetElement;
      if (!(targetElement !== null && targetElement !== void 0 && targetElement.contains(event.currentTarget.activeElement))) {
        if (this.isOpenInternal || this.isOpen) {
          this.isOpenInternal = false;
          this.$emit('update:is-open', false);
        }
      }
    },
    closeMenu() {
      if (this.isOpenInternal || this.isOpen) {
        this.isOpenInternal = false;
        this.$emit('update:is-open', false);
      }
    },
    handleCloseOnEsc(event) {
      if (event.key === 'Escape') {
        this.closeMenu();
      }
    }
  },
  watch: {
    isOpen(newValue) {
      if (!newValue) {
        this.shouldCloseMenu = false;
      }
      if (newValue === this.isOpenInternal) {
        return;
      }
      this.isOpenInternal = newValue;
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
    window.addEventListener('resize', this.closeMenu);
    window.addEventListener('keydown', this.handleCloseOnEsc);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
    window.removeEventListener('resize', this.closeMenu);
    window.removeEventListener('keydown', this.handleCloseOnEsc);
  }
};

/* script */
const __vue_script__$a = script$a;

/* template */
var __vue_render__$a = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    ref: "targetElement",
    staticClass: "menu",
    attrs: {
      "id": _vm.id
    },
    on: {
      "mouseleave": _vm.onMouseLeave,
      "mouseover": _vm.onMouseOver
    }
  }, [_c('div', {
    directives: [{
      name: "show",
      rawName: "v-show",
      value: _vm.isActive,
      expression: "isActive"
    }],
    staticClass: "menu__active-bar"
  }), _vm._v(" "), _c('MenuButton', {
    attrs: {
      "title": _vm.title,
      "is-openable": _vm.isOpenable,
      "disable-hover": _vm.disableHover,
      "size": _vm.buttonSize
    },
    on: {
      "click": _vm.onClickButton,
      "hover": _vm.onHoverButton
    }
  }, [_vm._t("button"), _vm._v(" "), _vm.notifications ? _c('Badge', {
    attrs: {
      "count": _vm.notifications
    }
  }) : _vm._e()], 2), _vm._v(" "), _vm._t("default", null, {
    "isOpen": _vm.isOpenInternal
  })], 2);
};
var __vue_staticRenderFns__$a = [];

/* style */
const __vue_inject_styles__$a = function (inject) {
  if (!inject) return;
  inject("data-v-623240ca_0", {
    source: ".menu[data-v-623240ca]{position:relative;height:100%;display:flex;align-items:center}.menu__active-bar[data-v-623240ca]{background:var(--text);position:absolute;height:2px;width:100%;bottom:2px}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$a = "data-v-623240ca";
/* module identifier */
const __vue_module_identifier__$a = undefined;
/* functional template */
const __vue_is_functional_template__$a = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$b = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$a,
  staticRenderFns: __vue_staticRenderFns__$a
}, __vue_inject_styles__$a, __vue_script__$a, __vue_scope_id__$a, __vue_is_functional_template__$a, __vue_module_identifier__$a, false, createInjector, undefined, undefined);
var Menu = __vue_component__$b;

//
//
//
//
//
//

var script$9 = {
  name: 'MenuOptions',
  props: {
    width: {
      type: Number,
      required: false,
      default: 280
    },
    alignRight: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      translationX: 0
    };
  },
  computed: {
    style() {
      return {
        width: `${this.width}px`,
        transform: `translateX(${this.translationX}px)`,
        right: this.alignRight ? 0 : 'auto'
      };
    }
  },
  mounted() {
    this.checkOverflow();
    window.addEventListener('resize', this.checkOverflow);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkOverflow);
  },
  methods: {
    checkOverflow() {
      if (this.alignRight) {
        return;
      }
      const element = this.$parent.$el;
      const elementRect = element.getBoundingClientRect();
      const windowWidth = window.innerWidth;

      // Check if the element is going to render outside the page
      if (elementRect.x + this.width > windowWidth) {
        // Calculate pixels to show element on the other side
        this.translationX = -(this.width - elementRect.width);
      } else {
        // Reset translation if there is no overflow
        this.translationX = 0;
      }
    }
  }
};

/* script */
const __vue_script__$9 = script$9;

/* template */
var __vue_render__$9 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('ul', {
    staticClass: "menu-options",
    style: _vm.style
  }, [_vm._t("default")], 2);
};
var __vue_staticRenderFns__$9 = [];

/* style */
const __vue_inject_styles__$9 = function (inject) {
  if (!inject) return;
  inject("data-v-10a13360_0", {
    source: ".menu-options[data-v-10a13360]{list-style:none;display:flex;flex-direction:column;position:absolute;top:75px;padding:8px;box-shadow:0 1px 2px 0 rgba(0,0,0,.1),0 0 1px 0 rgba(0,0,0,.35);background:var(--card);border-radius:4px}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$9 = "data-v-10a13360";
/* module identifier */
const __vue_module_identifier__$9 = undefined;
/* functional template */
const __vue_is_functional_template__$9 = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$a = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$9,
  staticRenderFns: __vue_staticRenderFns__$9
}, __vue_inject_styles__$9, __vue_script__$9, __vue_scope_id__$9, __vue_is_functional_template__$9, __vue_module_identifier__$9, false, createInjector, undefined, undefined);
var MenuOptions = __vue_component__$a;

//
//
//
//
//
//
//
//
//
//

var script$8 = {
  name: 'MenuOption',
  emits: ['click'],
  props: {
    title: {
      type: String,
      required: false
    },
    dividerTop: {
      type: Boolean,
      default: false
    }
  }
};

/* script */
const __vue_script__$8 = script$8;

/* template */
var __vue_render__$8 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('li', {
    staticClass: "menu-option"
  }, [_vm.dividerTop ? _c('hr', {
    staticClass: "menu-option__divider"
  }) : _vm._e(), _vm._v(" "), _c('a', _vm._g(_vm._b({
    staticClass: "menu-option__button"
  }, 'a', _vm.$attrs, false), _vm.$listeners), [_vm.title ? _c('p', [_vm._v(_vm._s(_vm.title))]) : _vm._e(), _vm._v(" "), _vm._t("default")], 2)]);
};
var __vue_staticRenderFns__$8 = [];

/* style */
const __vue_inject_styles__$8 = function (inject) {
  if (!inject) return;
  inject("data-v-290d80c2_0", {
    source: ".menu-option[data-v-290d80c2]{width:100%}.menu-option__divider[data-v-290d80c2]{margin:0;border:none;border-top:1px solid var(--background);width:100%;margin:8px 0}.menu-option__button[data-v-290d80c2]{width:100%;border:none;background:var(--transparent);color:var(--text);cursor:pointer;text-align:left;padding:8px 16px;display:flex;justify-content:space-between;align-items:center;min-height:40px;border-radius:4px;outline:0;transition:background .2s ease-in-out}.menu-option__button[data-v-290d80c2]:focus-visible,.menu-option__button[data-v-290d80c2]:hover{background:var(--over-card-hover)}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$8 = "data-v-290d80c2";
/* module identifier */
const __vue_module_identifier__$8 = undefined;
/* functional template */
const __vue_is_functional_template__$8 = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$9 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$8,
  staticRenderFns: __vue_staticRenderFns__$8
}, __vue_inject_styles__$8, __vue_script__$8, __vue_scope_id__$8, __vue_is_functional_template__$8, __vue_module_identifier__$8, false, createInjector, undefined, undefined);
var MenuOption = __vue_component__$9;

//
var script$7 = {
  name: 'OpenRolesMenu',
  apollo: {
    currentJobsInquiry: {
      query: JobsInquiryHiringsQuery$1
    }
  },
  components: {
    Menu,
    MenuOptions,
    MenuOption,
    Badge,
    ExternalLinkIcon
  },
  props: {
    openMenu: {
      type: String,
      default: ''
    }
  },
  computed: {
    isStaleInquiry() {
      var _this$currentJobsInqu;
      return ((_this$currentJobsInqu = this.currentJobsInquiry) === null || _this$currentJobsInqu === void 0 ? void 0 : _this$currentJobsInqu.stale) || false;
    },
    hiringsForReviewCount() {
      var _this$currentJobsInqu2;
      return ((_this$currentJobsInqu2 = this.currentJobsInquiry) === null || _this$currentJobsInqu2 === void 0 || (_this$currentJobsInqu2 = _this$currentJobsInqu2.hiringsForReview) === null || _this$currentJobsInqu2 === void 0 ? void 0 : _this$currentJobsInqu2.length) || 0;
    },
    ownsCurrentPage() {
      return [this.redirectRoutes.JOBS_PROFILE, this.redirectRoutes.JOBS_MY_MATCHES].some(to => this.$route.path.includes(to.identifier) || window.location.href.includes(to.identifier));
    }
  },
  emits: ['update:is-open'],
  methods: {
    onUpdateIsOpen(newValue, id) {
      this.$emit('update:is-open', newValue ? id : null);
    },
    handleClose() {
      this.$emit('update:is-open', null);
    },
    handleRedirect(event, toParams) {
      // enables opening in a new tab
      if (event.metaKey || event.ctrlKey) return;
      // prevents the default behavior of anchor tags to behave like a SPA
      event.preventDefault();
      emitRedirectEvent(toParams);
      this.handleClose();
    }
  },
  created() {
    this.redirectRoutes = routes;
    this.redirectPages = pages;
  }
};

/* script */
const __vue_script__$7 = script$7;

/* template */
var __vue_render__$7 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('Menu', {
    attrs: {
      "title": "Open roles",
      "notifications": _vm.hiringsForReviewCount,
      "id": "open-roles",
      "is-open": _vm.openMenu === 'open-roles',
      "is-active": _vm.ownsCurrentPage
    },
    on: {
      "update:is-open": function ($event) {
        return _vm.onUpdateIsOpen($event, 'open-roles');
      }
    },
    scopedSlots: _vm._u([{
      key: "default",
      fn: function (ref) {
        var isOpen = ref.isOpen;
        return [_c('transition', {
          attrs: {
            "name": "fade"
          }
        }, [_c('MenuOptions', {
          directives: [{
            name: "show",
            rawName: "v-show",
            value: isOpen,
            expression: "isOpen"
          }]
        }, [_vm.hiringsForReviewCount ? _c('MenuOption', {
          attrs: {
            "title": "New matches",
            "href": _vm.redirectRoutes.JOBS_MY_MATCHES.path
          },
          on: {
            "click": function (event) {
              return _vm.handleRedirect(event, _vm.redirectRoutes.JOBS_MY_MATCHES);
            }
          }
        }, [_c('Badge', {
          attrs: {
            "count": _vm.hiringsForReviewCount
          }
        })], 1) : _vm._e(), _vm._v(" "), _c('MenuOption', {
          attrs: {
            "title": "My applications",
            "href": _vm.redirectRoutes.JOBS_PROFILE.path
          },
          on: {
            "click": function (event) {
              return _vm.handleRedirect(event, _vm.redirectRoutes.JOBS_PROFILE);
            }
          }
        }), _vm._v(" "), _c('MenuOption', {
          staticClass: "open-roles-menu__update-profile",
          attrs: {
            "dividerTop": "",
            "title": "Update my Bravado profile",
            "href": _vm.redirectPages.ONBOARDING.path
          },
          on: {
            "click": function (event) {
              return _vm.handleRedirect(event, {
                path: _vm.redirectPages.ONBOARDING.path,
                newTab: true
              });
            }
          }
        }, [_c('ExternalLinkIcon', {
          staticClass: "open-roles-menu__icon"
        }), _vm._v(" "), _vm.isStaleInquiry ? _c('span', {
          staticClass: "open-roles-menu__warning"
        }, [_vm._v("Needs to be updated")]) : _vm._e()], 1)], 1)], 1)];
      }
    }])
  });
};
var __vue_staticRenderFns__$7 = [];

/* style */
const __vue_inject_styles__$7 = function (inject) {
  if (!inject) return;
  inject("data-v-b857badc_0", {
    source: ".open-roles-menu__update-profile a{display:block!important;position:relative}.open-roles-menu__icon{position:absolute;top:50%;right:16px;margin-top:-10px}.open-roles-menu__warning{color:var(--danger-accent);font-size:11px;display:block}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$7 = undefined;
/* module identifier */
const __vue_module_identifier__$7 = undefined;
/* functional template */
const __vue_is_functional_template__$7 = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$8 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$7,
  staticRenderFns: __vue_staticRenderFns__$7
}, __vue_inject_styles__$7, __vue_script__$7, __vue_scope_id__$7, __vue_is_functional_template__$7, __vue_module_identifier__$7, false, createInjector, undefined, undefined);
var OpenRolesMenu = __vue_component__$8;

const CommissionBalanceQuery = gql`
  query CommissionBalance {
    commissionBalance {
      currentBalance
      delta
    }
  }
`;
var CommissionBalanceQuery$1 = CommissionBalanceQuery;

var ScoreIcon = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"viewBox":"0 0 16 16","fill":"none","xmlns":"http://www.w3.org/2000/svg"}},[_c('path',{attrs:{"fill-rule":"evenodd","clip-rule":"evenodd","d":"M8 2.828L2.83 8 8 13.17 13.172 8 8 2.828zM1.414 6.586a2 2 0 000 2.828l5.172 5.172a2 2 0 002.828 0l5.172-5.172a2 2 0 000-2.828L9.414 1.414a2 2 0 00-2.828 0L1.414 6.586z","fill":"currentColor"}})]) } };

//
var script$6 = {
  name: 'CommissionBadge',
  components: {
    ScoreIcon
  },
  props: {
    count: {
      type: Number,
      default: 0,
      required: true
    }
  },
  computed: {
    formattedCount() {
      return new Intl.NumberFormat().format(this.count);
    }
  }
};

/* script */
const __vue_script__$6 = script$6;

/* template */
var __vue_render__$6 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "commission-badge",
    attrs: {
      "data-test": "commission-badge"
    }
  }, [_c('ScoreIcon', {
    attrs: {
      "width": "14",
      "height": "14"
    }
  }), _vm._v(" "), _c('span', [_vm._v("\n    " + _vm._s(_vm.formattedCount) + "\n  ")])], 1);
};
var __vue_staticRenderFns__$6 = [];

/* style */
const __vue_inject_styles__$6 = function (inject) {
  if (!inject) return;
  inject("data-v-defdf11c_0", {
    source: ".commission-badge[data-v-defdf11c]{font-size:13px;font-weight:600;line-height:20px;padding:0 4px;border-radius:4px;background:var(--over-card);color:var(--text);display:flex;align-items:center;justify-content:center}.commission-badge span[data-v-defdf11c]{margin-left:4px}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$6 = "data-v-defdf11c";
/* module identifier */
const __vue_module_identifier__$6 = undefined;
/* functional template */
const __vue_is_functional_template__$6 = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$7 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$6,
  staticRenderFns: __vue_staticRenderFns__$6
}, __vue_inject_styles__$6, __vue_script__$6, __vue_scope_id__$6, __vue_is_functional_template__$6, __vue_module_identifier__$6, false, createInjector, undefined, undefined);
var CommissionBadge = __vue_component__$7;

//
var script$5 = {
  name: 'CommunityMenu',
  apollo: {
    commissionBalance: {
      query: CommissionBalanceQuery$1,
      skip() {
        return !this.isAuthenticated;
      }
    }
  },
  components: {
    Menu,
    MenuOptions,
    MenuOption,
    CommissionBadge
  },
  props: {
    openMenu: {
      type: String,
      default: ''
    }
  },
  created() {
    this.redirectRoutes = routes;
    this.redirectPages = pages;
  },
  emits: ['update:is-open'],
  computed: {
    isAuthenticated() {
      var _this$$store;
      return this === null || this === void 0 || (_this$$store = this.$store) === null || _this$$store === void 0 || (_this$$store = _this$$store.$auth) === null || _this$$store === void 0 ? void 0 : _this$$store.loggedIn;
    },
    currentCommissionBalance() {
      var _this$commissionBalan;
      return (_this$commissionBalan = this.commissionBalance) === null || _this$commissionBalan === void 0 ? void 0 : _this$commissionBalan.currentBalance;
    },
    ownsCurrentPage() {
      return [this.redirectRoutes.WAR_ROOM, this.redirectRoutes.PRODUCTS, this.redirectRoutes.SALARIES, this.redirectPages.ACADEMY, this.redirectPages.TECH_SALES_U].some(to => this.$route.path.includes(to.identifier) || window.location.href.includes(to.identifier));
    }
  },
  methods: {
    onUpdateIsOpen(newValue, id) {
      this.$emit('update:is-open', newValue ? id : null);
    },
    handleClose() {
      this.$emit('update:is-open', null);
    },
    handleRedirect(event, params) {
      // enables opening in a new tab
      if (event.metaKey || event.ctrlKey) return;
      // prevents the default behavior of anchor tags to behave like a SPA
      event.preventDefault();
      emitRedirectEvent(params);
      this.handleClose();
    }
  }
};

/* script */
const __vue_script__$5 = script$5;

/* template */
var __vue_render__$5 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('Menu', {
    attrs: {
      "title": "Community",
      "id": "community",
      "is-open": _vm.openMenu === 'community',
      "is-active": _vm.ownsCurrentPage
    },
    on: {
      "update:is-open": function ($event) {
        return _vm.onUpdateIsOpen($event, 'community');
      }
    },
    scopedSlots: _vm._u([{
      key: "default",
      fn: function (ref) {
        var isOpen = ref.isOpen;
        return [_c('transition', {
          attrs: {
            "name": "fade"
          }
        }, [_c('MenuOptions', {
          directives: [{
            name: "show",
            rawName: "v-show",
            value: isOpen,
            expression: "isOpen"
          }]
        }, [_c('MenuOption', {
          attrs: {
            "title": "War Room",
            "href": _vm.redirectRoutes.WAR_ROOM.path
          },
          on: {
            "click": function (event) {
              return _vm.handleRedirect(event, {
                path: _vm.redirectRoutes.WAR_ROOM.path
              });
            }
          }
        }), _vm._v(" "), _c('MenuOption', {
          attrs: {
            "title": "Academy",
            "href": _vm.redirectPages.ACADEMY.path,
            "target": "_blank"
          },
          on: {
            "click": function (event) {
              return _vm.handleRedirect(event, {
                path: _vm.redirectPages.ACADEMY.path,
                newTab: true,
                sameOrigin: false
              });
            }
          }
        }), _vm._v(" "), _c('MenuOption', {
          attrs: {
            "title": "Tech Sales U",
            "href": _vm.redirectPages.TECH_SALES_U.path,
            "target": "_blank"
          },
          on: {
            "click": function (event) {
              return _vm.handleRedirect(event, {
                path: _vm.redirectPages.TECH_SALES_U.path,
                newTab: true,
                sameOrigin: false
              });
            }
          }
        }), _vm._v(" "), _vm.isAuthenticated ? _c('MenuOption', {
          attrs: {
            "title": "Commission store",
            "href": _vm.redirectRoutes.PRODUCTS.path
          },
          on: {
            "click": function (event) {
              return _vm.handleRedirect(event, {
                path: _vm.redirectRoutes.PRODUCTS.path
              });
            }
          }
        }, [_c('CommissionBadge', {
          attrs: {
            "count": _vm.currentCommissionBalance
          }
        })], 1) : _vm._e(), _vm._v(" "), _c('MenuOption', {
          attrs: {
            "title": "Salaries",
            "href": _vm.redirectRoutes.SALARIES.path
          },
          on: {
            "click": function (event) {
              return _vm.handleRedirect(event, {
                path: _vm.redirectRoutes.SALARIES.path,
                replacePage: true
              });
            }
          }
        }), _vm._v(" "), _vm.isAuthenticated ? _c('MenuOption', {
          attrs: {
            "title": "SalesGPT",
            "href": _vm.redirectPages.SALES_GPT.path,
            "target": "_blank"
          },
          on: {
            "click": function (event) {
              return _vm.handleRedirect(event, {
                path: _vm.redirectPages.SALES_GPT.path,
                newTab: true,
                sameOrigin: false
              });
            }
          }
        }) : _vm._e()], 1)], 1)];
      }
    }])
  });
};
var __vue_staticRenderFns__$5 = [];

/* style */
const __vue_inject_styles__$5 = undefined;
/* scoped */
const __vue_scope_id__$5 = undefined;
/* module identifier */
const __vue_module_identifier__$5 = undefined;
/* functional template */
const __vue_is_functional_template__$5 = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$6 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$5,
  staticRenderFns: __vue_staticRenderFns__$5
}, __vue_inject_styles__$5, __vue_script__$5, __vue_scope_id__$5, __vue_is_functional_template__$5, __vue_module_identifier__$5, false, undefined, undefined, undefined);
var CommunityMenu = __vue_component__$6;

//
var script$4 = {
  name: 'ForEmployersMenu',
  components: {
    Menu,
    MenuOptions,
    MenuOption
  },
  props: {
    openMenu: {
      type: String,
      default: ''
    }
  },
  created() {
    this.redirectRoutes = routes;
    this.redirectPages = pages;
  },
  emits: ['update:is-open'],
  methods: {
    onUpdateIsOpen(newValue, id) {
      this.$emit('update:is-open', newValue ? id : null);
    },
    handleClose() {
      this.$emit('update:is-open', null);
    },
    handleRedirect(event, params) {
      // enables opening in a new tab
      if (event.metaKey || event.ctrlKey) return;
      // prevents the default behavior of anchor tags to behave like a SPA
      event.preventDefault();
      emitRedirectEvent(params);
      this.handleClose();
    }
  }
};

/* script */
const __vue_script__$4 = script$4;

/* template */
var __vue_render__$4 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('Menu', {
    attrs: {
      "title": "For Employers",
      "id": "employers",
      "is-open": _vm.openMenu === 'employers'
    },
    on: {
      "update:is-open": function ($event) {
        return _vm.onUpdateIsOpen($event, 'employers');
      }
    },
    scopedSlots: _vm._u([{
      key: "default",
      fn: function (ref) {
        var isOpen = ref.isOpen;
        return [_c('transition', {
          attrs: {
            "name": "fade"
          }
        }, [_c('MenuOptions', {
          directives: [{
            name: "show",
            rawName: "v-show",
            value: isOpen,
            expression: "isOpen"
          }]
        }, [_c('MenuOption', {
          attrs: {
            "title": "Hire sales talent",
            "href": _vm.redirectPages.TALENT_LANDING.path
          },
          on: {
            "click": function (event) {
              return _vm.handleRedirect(event, {
                path: _vm.redirectPages.TALENT_LANDING.path,
                newTab: true,
                sameOrigin: false
              });
            }
          }
        }), _vm._v(" "), _c('MenuOption', {
          attrs: {
            "title": "Executive search",
            "href": _vm.redirectPages.EXEC_SEARCH_LANDING.path,
            "target": "_blank"
          },
          on: {
            "click": function (event) {
              return _vm.handleRedirect(event, {
                path: _vm.redirectPages.EXEC_SEARCH_LANDING.path,
                newTab: true,
                sameOrigin: false
              });
            }
          }
        })], 1)], 1)];
      }
    }])
  });
};
var __vue_staticRenderFns__$4 = [];

/* style */
const __vue_inject_styles__$4 = undefined;
/* scoped */
const __vue_scope_id__$4 = undefined;
/* module identifier */
const __vue_module_identifier__$4 = undefined;
/* functional template */
const __vue_is_functional_template__$4 = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$5 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$4,
  staticRenderFns: __vue_staticRenderFns__$4
}, __vue_inject_styles__$4, __vue_script__$4, __vue_scope_id__$4, __vue_is_functional_template__$4, __vue_module_identifier__$4, false, undefined, undefined, undefined);
var ForEmployersMenu = __vue_component__$5;

var SpinnerIcon = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"viewBox":"0 0 24 24","fill":"none","xmlns":"http://www.w3.org/2000/svg"}},[_c('g',{attrs:{"stroke":"currentColor","stroke-width":"3"}},[_c('circle',{attrs:{"opacity":".2","cx":"12","cy":"12","r":"9.75"}}),_c('path',{attrs:{"d":"M21.75 12A9.75 9.75 0 0012 2.25","stroke-linecap":"round","stroke-linejoin":"round"}})])]) } };

//
var script$3 = {
  name: 'LoadingBlobs',
  components: {
    ChevronDownIcon,
    SpinnerIcon
  }
};

/* script */
const __vue_script__$3 = script$3;

/* template */
var __vue_render__$3 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "loading-blobs"
  }, [_c('span', {
    staticClass: "sr-only"
  }, [_vm._v("Loading...")]), _vm._v(" "), _vm._m(0), _vm._v(" "), _vm._m(1), _vm._v(" "), _c('div', {
    staticClass: "user-loading"
  }, [_c('div', {
    staticClass: "avatar"
  }, [_c('SpinnerIcon', {
    staticClass: "spinner",
    attrs: {
      "height": "16",
      "width": "16"
    }
  })], 1), _vm._v(" "), _c('i', {
    staticClass: "chevron-down"
  }, [_c('ChevronDownIcon')], 1)])]);
};
var __vue_staticRenderFns__$3 = [function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "blob-loading"
  }, [_c('span')]);
}, function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "blob-loading"
  }, [_c('span')]);
}];

/* style */
const __vue_inject_styles__$3 = function (inject) {
  if (!inject) return;
  inject("data-v-d1e80132_0", {
    source: ".loading-blobs[data-v-d1e80132]{display:flex;align-items:center;justify-content:center;gap:6px}.loading-blobs .blob-loading[data-v-d1e80132]{display:flex;align-items:center;justify-content:center;width:38px;height:38px}.loading-blobs .blob-loading span[data-v-d1e80132]{width:20px;height:20px;border-radius:50%;background-color:var(--over-card-hover)}.loading-blobs .user-loading[data-v-d1e80132]{display:flex;align-items:center;justify-content:center;padding:10px;gap:8px;pointer-events:none}.loading-blobs .user-loading .avatar[data-v-d1e80132]{display:flex;align-items:center;justify-content:center;width:40px;height:40px;background-color:var(--over-card-hover);border-radius:4px}@media only screen and (max-width:767px){.loading-blobs .user-loading .avatar[data-v-d1e80132]{width:32px;height:32px}}.loading-blobs .user-loading .chevron-down[data-v-d1e80132]{width:16px;height:8px;display:flex;align-items:center;justify-content:center}@keyframes spinner-data-v-d1e80132{0%{transform:rotate(0)}100%{transform:rotate(360deg)}}.loading-blobs .user-loading .spinner[data-v-d1e80132]{animation:1.5s linear infinite spinner-data-v-d1e80132}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$3 = "data-v-d1e80132";
/* module identifier */
const __vue_module_identifier__$3 = undefined;
/* functional template */
const __vue_is_functional_template__$3 = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$4 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$3,
  staticRenderFns: __vue_staticRenderFns__$3
}, __vue_inject_styles__$3, __vue_script__$3, __vue_scope_id__$3, __vue_is_functional_template__$3, __vue_module_identifier__$3, false, createInjector, undefined, undefined);
var LoadingBlobs = __vue_component__$4;

var ArrowRightIcon = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"width":"24","height":"24","fill":"none","xmlns":"http://www.w3.org/2000/svg"}},[_c('path',{attrs:{"d":"M17.031 12.531l-7.5 7.5A.75.75 0 118.47 18.97L15.44 12 8.47 5.031A.75.75 0 119.53 3.97l7.5 7.5a.748.748 0 010 1.061z","fill":"currentColor"}})]) } };

//
var script$2 = {
  name: 'AuthenticationSide',
  components: {
    ArrowRightIcon
  },
  methods: {
    handleLogIn() {
      emitRedirectEvent({
        path: `${routes.LOGIN_EXTERNAL_SIGN_IN.path}&referer=${window.location.pathname}${window.location.search}`
      });
    },
    handleSignUp() {
      emitRedirectEvent({
        path: `${routes.LOGIN_EXTERNAL_SIGN_UP.path}&referer=${window.location.pathname}${window.location.search}`
      });
    }
  }
};

/* script */
const __vue_script__$2 = script$2;

/* template */
var __vue_render__$2 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "authentication-side"
  }, [_c('button', {
    staticClass: "authentication-side__login",
    attrs: {
      "data-test": "authentication-button-login"
    },
    on: {
      "click": _vm.handleLogIn
    }
  }, [_vm._v("\n    Log In\n  ")]), _vm._v(" "), _c('button', {
    staticClass: "authentication-side__signup",
    attrs: {
      "data-test": "authentication-button-signup"
    },
    on: {
      "click": _vm.handleSignUp
    }
  }, [_c('span', [_vm._v(" Sign Up ")]), _vm._v(" "), _c('span', {
    staticClass: "button-icon"
  }, [_c('ArrowRightIcon')], 1)])]);
};
var __vue_staticRenderFns__$2 = [];

/* style */
const __vue_inject_styles__$2 = function (inject) {
  if (!inject) return;
  inject("data-v-4024fa8c_0", {
    source: ".authentication-side[data-v-4024fa8c]{display:flex;align-items:center;gap:12px}.authentication-side button[data-v-4024fa8c]{display:flex;align-items:center;justify-content:center;padding:1px 16px 2px;height:40px;border-radius:4px;font-size:15px;font-weight:600;line-height:24px;cursor:pointer;transition-timing-function:ease-in-out;transition-duration:.2s;transition-property:color,background-color,border-color,box-shadow;outline:2px solid var(--transparent);outline-offset:-1px;border:1px solid var(--border);white-space:nowrap}.authentication-side button[data-v-4024fa8c]:focus-visible:not([disabled]){border-color:transparent;outline-color:var(--blue-60)}.authentication-side__login[data-v-4024fa8c],.authentication-side__signup[data-v-4024fa8c]{color:var(--text)}.authentication-side__login[data-v-4024fa8c]:focus-visible:not([disabled]),.authentication-side__login[data-v-4024fa8c]:hover:not([disabled]),.authentication-side__signup[data-v-4024fa8c]:focus-visible:not([disabled]),.authentication-side__signup[data-v-4024fa8c]:hover:not([disabled]){border-color:transparent;background-color:var(--over-card-hover)}@media screen and (max-width:992px){.authentication-side button[data-v-4024fa8c]{padding:8px 10px;font-size:13px}.authentication-side__login[data-v-4024fa8c]{display:none!important}}.authentication-side .button-icon[data-v-4024fa8c]{display:none}@media screen and (min-width:1280px){.authentication-side .button-icon[data-v-4024fa8c]{display:flex;margin-left:4px;margin-right:-4px}}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$2 = "data-v-4024fa8c";
/* module identifier */
const __vue_module_identifier__$2 = undefined;
/* functional template */
const __vue_is_functional_template__$2 = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$3 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$2,
  staticRenderFns: __vue_staticRenderFns__$2
}, __vue_inject_styles__$2, __vue_script__$2, __vue_scope_id__$2, __vue_is_functional_template__$2, __vue_module_identifier__$2, false, createInjector, undefined, undefined);
var AuthenticationSide = __vue_component__$3;

var LogoDesktop = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"width":"118","height":"20","fill":"none","xmlns":"http://www.w3.org/2000/svg"}},[_c('path',{attrs:{"fill-rule":"evenodd","clip-rule":"evenodd","d":"M31.192 4.5c2.31 0 3.75 1.005 3.75 2.625 0 1.125-.69 2.01-1.815 2.31 1.365.255 2.19 1.275 2.19 2.655 0 1.8-1.53 2.91-3.96 2.91h-4.994V4.5h4.83zm-.12 4.23c.9 0 1.44-.435 1.44-1.17 0-.72-.54-1.14-1.44-1.125h-2.34V8.73h2.34zm0 4.335c1.11 0 1.8-.465 1.8-1.275 0-.765-.69-1.23-1.8-1.215h-2.34v2.49h2.34zM48.678 15h-2.685l-1.665-3h-2.415v3h-2.37V4.5h4.56c2.745 0 4.305 1.32 4.305 3.645 0 1.62-.69 2.79-1.965 3.39L48.678 15zm-6.765-8.535v3.555h2.19c1.305 0 2.055-.6 2.055-1.8 0-1.17-.75-1.755-2.055-1.755h-2.19zM60.657 15l-.825-2.04h-4.86L54.147 15h-2.446l4.56-10.5h2.446L63.193 15h-2.535zm-4.905-3.945h3.315l-1.65-4.08-1.665 4.08zM64.838 4.5L68.963 15h2.445l4.17-10.5h-2.445l-2.88 7.92-2.865-7.92h-2.55zM86.193 15l-.825-2.04h-4.86L79.683 15h-2.445l4.56-10.5h2.445L88.728 15h-2.535zm-4.906-3.945h3.316l-1.65-4.08-1.666 4.08zM96.777 4.5c3.256 0 5.521 2.145 5.521 5.25 0 3.09-2.295 5.25-5.61 5.25h-4.365V4.5h4.455zm.06 8.505c1.756 0 3.046-1.32 3.046-3.24 0-1.935-1.35-3.27-3.15-3.27h-2.04v6.51h2.145zm14.411-8.58c3.21 0 5.64 2.28 5.64 5.325 0 3.03-2.43 5.355-5.64 5.355-3.21 0-5.64-2.31-5.64-5.355 0-3.06 2.43-5.325 5.64-5.325zm.03 2.07a3.23 3.23 0 00-3.24 3.255c0 1.845 1.485 3.285 3.24 3.285 1.755 0 3.18-1.44 3.18-3.285 0-1.845-1.425-3.255-3.18-3.255z","fill":"currentColor"}}),_c('path',{attrs:{"d":"M16.666 3.333v2.25c0 .25 0 .417-.083.667-.25.916-1.166 1.583-2.083 1.583H5.583c-1 0-1.833-.667-2.083-1.583-.167-.25-.167-.5-.167-.667v-2.25h2.25v1.333c0 .5.417.917.917.917h7.083c.5 0 .917-.417.917-.917V3.333h2.166zM13 10l-2.417 4.417H9.417L7 10H3.333l1.25 2.25h1.084l2.416 4.416h3.833l2.417-4.416h1.083l1.25-2.25H13z","fill":"currentColor"}})]) } };

var LogoMobile = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"width":"125","height":"22","fill":"none","xmlns":"http://www.w3.org/2000/svg"}},[_c('path',{attrs:{"fill-rule":"evenodd","clip-rule":"evenodd","d":"M33.183 5.148c2.458 0 3.99 1.07 3.99 2.793 0 1.197-.735 2.138-1.931 2.457 1.452.271 2.33 1.357 2.33 2.825 0 1.914-1.628 3.095-4.213 3.095h-5.314V5.148h5.138zm-.127 4.5c.957 0 1.532-.463 1.532-1.245 0-.765-.575-1.212-1.532-1.196h-2.49v2.441h2.49zm0 4.612c1.18 0 1.914-.495 1.914-1.357 0-.813-.734-1.308-1.914-1.292h-2.49v2.649h2.49zm18.728 2.058h-2.856l-1.771-3.191h-2.57v3.191h-2.52V5.148h4.85c2.92 0 4.58 1.404 4.58 3.878 0 1.723-.734 2.968-2.09 3.606l2.377 3.686zm-7.197-9.08v3.782h2.33c1.389 0 2.186-.638 2.186-1.914 0-1.245-.797-1.867-2.186-1.867h-2.33zm19.942 9.08l-.878-2.17h-5.17l-.878 2.17h-2.6l4.85-11.17h2.602l4.77 11.17H64.53zm-5.218-4.196h3.526l-1.755-4.34-1.771 4.34zm9.665-6.974l4.388 11.17h2.601l4.436-11.17h-2.6l-3.065 8.426-3.047-8.426h-2.713zm22.718 11.17l-.878-2.17h-5.17l-.878 2.17h-2.6l4.85-11.17h2.601l4.772 11.17h-2.697zm-5.218-4.196h3.526l-1.755-4.34-1.771 4.34zm16.478-6.974c3.463 0 5.873 2.282 5.873 5.585 0 3.287-2.442 5.585-5.968 5.585h-4.644V5.148h4.739zm.064 9.048c1.867 0 3.24-1.404 3.24-3.447 0-2.058-1.436-3.479-3.351-3.479h-2.171v6.926h2.282zm15.33-9.128c3.415 0 6 2.426 6 5.665 0 3.224-2.585 5.697-6 5.697s-6-2.457-6-5.697c0-3.255 2.585-5.665 6-5.665zm.032 2.202a3.436 3.436 0 00-3.447 3.463c0 1.963 1.58 3.495 3.447 3.495s3.383-1.532 3.383-3.495-1.516-3.463-3.383-3.463z","fill":"currentColor"}}),_c('path',{attrs:{"d":"M13.83 11l-2.572 4.698h-1.24L7.446 11H3.546l1.33 2.394h1.152L8.6 18.092h4.078l2.57-4.698H16.4L17.73 11h-3.9z","fill":"currentColor"}}),_c('path',{attrs:{"fill-rule":"evenodd","clip-rule":"evenodd","d":"M17.73 3.907v2.364c0 .241-.038.478-.104.7v.005a2.375 2.375 0 01-2.26 1.66H5.91c-1.06 0-1.957-.7-2.26-1.66V6.97a2.457 2.457 0 01-.104-.7V3.907H5.91v1.419c0 .52.425.945.946.945h7.565c.52 0 .945-.425.945-.945V3.907h2.364z","fill":"currentColor"}})]) } };

//
var script$1 = {
  name: 'Logo',
  components: {
    LogoDesktop,
    LogoMobile
  },
  data() {
    return {
      homeRoute: routes.HOME.path
    };
  },
  methods: {
    handleClickLogo(event) {
      // enables opening in a new tab
      if (event.metaKey || event.ctrlKey) return;
      // prevents the default behavior of anchor tags to behave like a SPA
      event.preventDefault();
      emitRedirectEvent({
        path: this.homeRoute
      });
    }
  }
};

/* script */
const __vue_script__$1 = script$1;

/* template */
var __vue_render__$1 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('a', {
    staticClass: "bravado-nav__logo",
    attrs: {
      "aria-label": "Go to main page",
      "href": _vm.homeRoute
    },
    on: {
      "click": _vm.handleClickLogo
    }
  }, [_c('LogoDesktop', {
    staticClass: "desktop-logo",
    attrs: {
      "alt": "Bravado Logo"
    }
  }), _vm._v(" "), _c('LogoMobile', {
    staticClass: "mobile-logo",
    attrs: {
      "alt": "Bravado Logo"
    }
  })], 1);
};
var __vue_staticRenderFns__$1 = [];

/* style */
const __vue_inject_styles__$1 = function (inject) {
  if (!inject) return;
  inject("data-v-2fc71aec_0", {
    source: ":root .bravado-nav{--transparent:transparent;--black:#000;--gray-90:#0e0e0e;--gray-80:#181818;--gray-70:#56565b;--gray-50:#797b86;--gray-10:#f8f8f9;--blue-50:#3f74ff;--blue-60:#2360ff;--white:#fff;--red-30:#ed6c6c;--red-50:#f03838;--green:#2fb254;--green-50:#06cf87;--cyan-light:#d0dcfb;--cyan-light-2:#e9efff;--cyan-1:#224373;--cyan-2:#555fa3;--cyan-3:#8a92ce;--cyan-4:#c6cbed;--purple-1:#5d3251;--purple-2:#635563;--purple-3:#af8fb6;--purple-4:#dcd2dc;--gold-1:#c67702;--gold-2:#e0a029;--gold-3:#dfb77a;--gold-4:#eddfc5;--gold-5:#f9f6f0;--gold-20:#fbe9ba;--gold-40:#dfb77a;--background:#f7f7f7;--card:#fff;--over-card:#f8f8f9;--over-card-hover:#e8e9ef;--text:#161a21;--text-hover:#2a2a2c;--sub-text:#45454a;--sub-text-2:#797b86;--border-color:#d6d8e1;--transparent-text-a08:rgba(14, 14, 14, 0.08);--border:rgba(0, 0, 0, 0.1);--danger-accent:#c34b3f;--elevation-01:0px 0px 1px rgba(0, 0, 0, 0.35),0px 1px 2px rgba(0, 0, 0, 0.1);--elevation-05:0px 0px 1px rgba(0, 0, 0, 0.35),0px 4px 10px rgba(0, 0, 0, 0.1);--elevation-10:0px 8px 8px 0px rgba(0, 0, 0, 0.08),0px 0px 4px 0px rgba(0, 0, 0, 0.16);--elevation-15:0px 0px 1px rgba(0, 0, 0, 0.35),0px 8px 16px rgba(0, 0, 0, 0.04),0px 16px 16px rgba(0, 0, 0, 0.08);--page-max-width:1280px}.bravado-nav{font-size:15px}.bravado-nav p{margin:0}.bravado-nav .sr-only{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border-width:0}.bravado-theme--dark .bravado-nav,[data-color-mode-forced=dark] .bravado-nav,html.dark .bravado-nav{--background:#3f3f41;--card:#0e0e0e;--over-card:#181818;--over-card-hover:#39393d;--text:#e3e9ef;--text-hover:#fff;--sub-text:#abb0bd;--sub-text-2:#c8cbd3;--border-color:#6a6a72;--transparent-text-a08:rgba(255, 255, 255, 0.08)}.desktop-logo{display:none}@media only screen and (min-width:768px){.desktop-logo{display:unset}}.mobile-logo{display:unset}@media only screen and (min-width:768px){.mobile-logo{display:none}}.bravado-nav__logo{color:var(--text);display:flex;margin:0;margin-left:4px;margin-right:auto;padding:0}@media only screen and (max-width:767px){.bravado-nav__logo{flex:1}}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$1 = undefined;
/* module identifier */
const __vue_module_identifier__$1 = undefined;
/* functional template */
const __vue_is_functional_template__$1 = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$2 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1,
  staticRenderFns: __vue_staticRenderFns__$1
}, __vue_inject_styles__$1, __vue_script__$1, __vue_scope_id__$1, __vue_is_functional_template__$1, __vue_module_identifier__$1, false, createInjector, undefined, undefined);
var Logo = __vue_component__$2;

//
var script = {
  name: 'BravadoNetworkNav',
  components: {
    Logo,
    OpenRolesMenu,
    CommunityMenu,
    LoadingBlobs,
    ForEmployersMenu,
    AuthenticationSide,
    MobileMenu: () => import('./index-50cf7a06.js').then(d => d.default),
    UserMenu: () => import('./index-b95632eb.js').then(d => d.default),
    SearchMenu: () => import('./index-88cee250.js').then(d => d.default),
    NotificationMenu: () => import('./index-e1f97d67.js').then(d => d.default),
    MenuButton: () => Promise.resolve().then(function () { return index; }).then(d => d.default)
  },
  props: {
    isLoading: {
      type: Boolean,
      default: false
    },
    shouldUseRouter: {
      type: Boolean,
      default: true
    },
    onlyLogo: {
      type: Boolean,
      default: false
    }
  },
  mounted() {
    GlobalEmitter.$on(PORTFOLIO_REFETCH_EVENT, this.bypassPortfolioRefetchEvent);
    GlobalEmitter.$on(BRAVADO_NAV_REDIRECT, this.handleRedirect);
  },
  beforeMount() {
    GlobalEmitter.$off(PORTFOLIO_REFETCH_EVENT, this.bypassPortfolioRefetchEvent);
    GlobalEmitter.$off(BRAVADO_NAV_REDIRECT, this.handleRedirect);
  },
  data() {
    return {
      openMenu: '',
      searchMenuOpened: false
    };
  },
  computed: {
    isMobile() {
      return this.$breakpoint.xs;
    },
    isMobileSearchMenuOpened() {
      return this.isMobile && this.searchMenuOpened;
    },
    isAuthenticated() {
      return this.$store.$auth.loggedIn;
    },
    isMobileMenuOpen() {
      return this.openMenu && this.openMenu === 'mobile';
    }
  },
  methods: {
    bypassPortfolioRefetchEvent({
      slug
    }) {
      this.$emit('portfolio:refetch', slug);
    },
    onUpdateIsOpen(id) {
      this.openMenu = id || '';
    },
    onSearchMenuToggle(state) {
      this.searchMenuOpened = state;
    },
    handleGuestOpenRoles(event) {
      // enables opening in a new tab
      if (event.metaKey || event.ctrlKey) return;
      // prevents the default behavior of anchor tags to behave like a SPA
      event.preventDefault();
      this.handleRedirect({
        path: routes.JOBS.path
      });
    },
    handleOpenNewTab({
      path,
      sameOrigin
    }) {
      if (sameOrigin) {
        window.open(`${window.location.origin}${path}`, '_blank');
        return;
      }
      window.open(path, '_blank');
    },
    handleReplacePage({
      path,
      sameOrigin
    }) {
      if (sameOrigin) {
        window.location = `${window.location.origin}${path}`;
        return;
      }
      window.location = path;
    },
    handleRedirect({
      path,
      sameOrigin,
      newTab,
      replacePage
    }) {
      if (newTab) {
        return this.handleOpenNewTab({
          path,
          sameOrigin
        });
      }
      if (replacePage || !this.shouldUseRouter) {
        return this.handleReplacePage({
          path,
          sameOrigin
        });
      }
      return this.$router.push(path);
    }
  },
  created() {
    this.routes = routes;
  }
};

/* script */
const __vue_script__ = script;

/* template */
var __vue_render__ = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bravado-nav"
  }, [_c('div', {
    class: {
      'bravado-nav__container': true,
      'bravado-nav__container--fixed': _vm.isMobileMenuOpen
    }
  }, [_vm.onlyLogo ? _c('Logo') : [!_vm.isMobileSearchMenuOpened ? [_c('div', {
    staticClass: "bravado-nav__item bravado-nav__menus-mobile",
    class: {
      'bravado-nav__disabled': _vm.openMenu && _vm.openMenu !== 'mobile'
    }
  }, [_c('MobileMenu', {
    attrs: {
      "open-menu": _vm.openMenu
    },
    on: {
      "update:is-open": _vm.onUpdateIsOpen
    }
  })], 1), _vm._v(" "), _c('Logo')] : _vm._e(), _vm._v(" "), !_vm.searchMenuOpened ? _c('div', {
    staticClass: "bravado-nav__menus bravado-nav__fill bravado-nav__left-space"
  }, [_vm.isAuthenticated ? _c('OpenRolesMenu', {
    attrs: {
      "open-menu": _vm.openMenu
    },
    on: {
      "update:is-open": _vm.onUpdateIsOpen
    }
  }) : _c('MenuButton', {
    attrs: {
      "is-openable": false,
      "data-test": "open-roles-guest",
      "title": "Open roles",
      "disable-hover": "",
      "href": _vm.routes.JOBS.path
    },
    on: {
      "click": _vm.handleGuestOpenRoles
    }
  }), _vm._v(" "), _c('CommunityMenu', {
    attrs: {
      "open-menu": _vm.openMenu
    },
    on: {
      "update:is-open": _vm.onUpdateIsOpen
    }
  }), _vm._v(" "), _c('ForEmployersMenu', {
    attrs: {
      "open-menu": _vm.openMenu
    },
    on: {
      "update:is-open": _vm.onUpdateIsOpen
    }
  })], 1) : _vm._e(), _vm._v(" "), _c('div', {
    staticClass: "bravado-nav__user-menus",
    class: {
      'bravado-nav__fill bravado-nav__left-space': _vm.searchMenuOpened
    }
  }, [_vm.isLoading ? _c('LoadingBlobs') : _vm.$store.$auth.loggedIn ? _c('div', {
    staticClass: "bravado-nav__user-menus--authenticated"
  }, [_c('div', {
    staticClass: "bravado-nav__item",
    class: {
      'bravado-nav__disabled': _vm.isMobileMenuOpen,
      'bravado-nav__fill': _vm.searchMenuOpened
    }
  }, [_c('SearchMenu', {
    on: {
      "update:is-open": _vm.onSearchMenuToggle
    }
  })], 1), _vm._v(" "), !_vm.isMobileSearchMenuOpened ? [_c('div', {
    staticClass: "bravado-nav__item",
    class: {
      'bravado-nav__disabled': _vm.isMobileMenuOpen
    }
  }, [!_vm.searchMenuOpened ? _c('NotificationMenu', {
    attrs: {
      "open-menu": _vm.openMenu
    },
    on: {
      "update:is-open": _vm.onUpdateIsOpen
    }
  }) : _vm._e()], 1), _vm._v(" "), _c('div', {
    staticClass: "bravado-nav__item",
    class: {
      'bravado-nav__disabled': _vm.isMobileMenuOpen
    }
  }, [_c('UserMenu', {
    attrs: {
      "open-menu": _vm.openMenu,
      "is-mobile": _vm.isMobile
    },
    on: {
      "update:is-open": _vm.onUpdateIsOpen
    }
  })], 1)] : _vm._e()], 2) : _c('div', {
    class: {
      'bravado-nav__disabled': _vm.isMobileMenuOpen
    }
  }, [_c('AuthenticationSide')], 1)], 1)]], 2)]);
};
var __vue_staticRenderFns__ = [];

/* style */
const __vue_inject_styles__ = function (inject) {
  if (!inject) return;
  inject("data-v-11167604_0", {
    source: ":root .bravado-nav{--transparent:transparent;--black:#000;--gray-90:#0e0e0e;--gray-80:#181818;--gray-70:#56565b;--gray-50:#797b86;--gray-10:#f8f8f9;--blue-50:#3f74ff;--blue-60:#2360ff;--white:#fff;--red-30:#ed6c6c;--red-50:#f03838;--green:#2fb254;--green-50:#06cf87;--cyan-light:#d0dcfb;--cyan-light-2:#e9efff;--cyan-1:#224373;--cyan-2:#555fa3;--cyan-3:#8a92ce;--cyan-4:#c6cbed;--purple-1:#5d3251;--purple-2:#635563;--purple-3:#af8fb6;--purple-4:#dcd2dc;--gold-1:#c67702;--gold-2:#e0a029;--gold-3:#dfb77a;--gold-4:#eddfc5;--gold-5:#f9f6f0;--gold-20:#fbe9ba;--gold-40:#dfb77a;--background:#f7f7f7;--card:#fff;--over-card:#f8f8f9;--over-card-hover:#e8e9ef;--text:#161a21;--text-hover:#2a2a2c;--sub-text:#45454a;--sub-text-2:#797b86;--border-color:#d6d8e1;--transparent-text-a08:rgba(14, 14, 14, 0.08);--border:rgba(0, 0, 0, 0.1);--danger-accent:#c34b3f;--elevation-01:0px 0px 1px rgba(0, 0, 0, 0.35),0px 1px 2px rgba(0, 0, 0, 0.1);--elevation-05:0px 0px 1px rgba(0, 0, 0, 0.35),0px 4px 10px rgba(0, 0, 0, 0.1);--elevation-10:0px 8px 8px 0px rgba(0, 0, 0, 0.08),0px 0px 4px 0px rgba(0, 0, 0, 0.16);--elevation-15:0px 0px 1px rgba(0, 0, 0, 0.35),0px 8px 16px rgba(0, 0, 0, 0.04),0px 16px 16px rgba(0, 0, 0, 0.08);--page-max-width:1280px}.bravado-nav{font-size:15px}.bravado-nav p{margin:0}.bravado-nav .sr-only{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border-width:0}.bravado-theme--dark .bravado-nav,[data-color-mode-forced=dark] .bravado-nav,html.dark .bravado-nav{--background:#3f3f41;--card:#0e0e0e;--over-card:#181818;--over-card-hover:#39393d;--text:#e3e9ef;--text-hover:#fff;--sub-text:#abb0bd;--sub-text-2:#c8cbd3;--border-color:#6a6a72;--transparent-text-a08:rgba(255, 255, 255, 0.08)}.bravado-nav{position:sticky;z-index:100;top:0;left:0;right:0;width:100%;border-bottom:1px solid var(--transparent-text-a08);background:var(--card)}.bravado-nav button{background:var(--transparent);border:none;cursor:pointer;transition-timing-function:ease-in-out;transition-duration:.2s;transition-property:color,background-color,border-color,box-shadow;outline:2px solid var(--transparent);outline-offset:2px;border-radius:4px}.bravado-nav button:focus-visible:not([disabled]){outline-color:var(--blue-60)}@media only screen and (max-width:767px){.bravado-nav__disabled{opacity:.2;pointer-events:none}}.bravado-nav__container{display:flex;align-items:center;justify-content:center;max-width:var(--page-max-width);margin:0 auto;padding:0 16px;height:71px;width:100%}.bravado-nav__container--fixed{position:fixed;top:0;background:var(--card)}@media only screen and (min-width:768px){.bravado-nav__container{gap:24px;padding:0 32px}}@media only screen and (min-width:768px){.bravado-nav__left-space{margin-left:12px}}@media only screen and (min-width:992px){.bravado-nav__left-space{margin-left:52px}}.bravado-nav__fill{flex:1}.bravado-nav__menus{align-items:center;gap:6px;display:none;height:100%}@media only screen and (min-width:768px){.bravado-nav__menus{display:flex!important}.bravado-nav__menus-mobile{display:none!important}}.bravado-nav__user-menus{display:flex;align-items:center;height:100%}.bravado-nav__user-menus__full-width{flex:1}.bravado-nav__user-menus--authenticated{display:flex;align-items:center;gap:6px;flex:1;height:100%}.bravado-nav__item{transition:opacity .2s ease-in-out;display:flex;align-items:center;height:100%}.fade-enter-active,.fade-leave-active{transition:all .3s}.fade-enter,.fade-leave-to{opacity:0;transform:translateY(-40px)}@media only screen and (min-width:768px){.fade-enter,.fade-leave-to{transform:translateY(0)}}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__ = undefined;
/* module identifier */
const __vue_module_identifier__ = undefined;
/* functional template */
const __vue_is_functional_template__ = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__ = /*#__PURE__*/normalizeComponent({
  render: __vue_render__,
  staticRenderFns: __vue_staticRenderFns__
}, __vue_inject_styles__, __vue_script__, __vue_scope_id__, __vue_is_functional_template__, __vue_module_identifier__, false, createInjector, undefined, undefined);
var __vue_component__$1 = __vue_component__;

var components = /*#__PURE__*/Object.freeze({
    __proto__: null,
    BravadoNetworkNav: __vue_component__$1
});

// Import vue components

// install function executed by Vue.use()
const install = function installBravadoNetworkNav(Vue) {
  Object.entries(components).forEach(([componentName, component]) => {
    Vue.component(componentName, component);
  });
};

// Create module definition for Vue.use()
var entry_esm = {
  install
};

export { Badge as B, ChevronDownIcon as C, JobsInquiryHiringsQuery$1 as J, Menu as M, __vue_component__$1 as _, CommissionBalanceQuery$1 as a, MenuOptions as b, createInjector as c, CommissionBadge as d, emitRedirectEvent as e, MenuOption as f, gql as g, emitPortfolioRefetchEvent as h, entry_esm as i, normalizeComponent as n, pages as p, routes as r };
